/**
 * Native personalizer: replaces the Zepto Product Personalizer app.
 * Defines the <native-personalizer> element only. Dependency-free.
 *
 * Contract + design: docs/native-personalizer/BUILD.md (§2 ground truth, §3 frozen contract, P2.3).
 * Byte-identical order output to Zepto is a HARD requirement.
 *
 * Branching mechanism: each type is a <fieldset disabled data-np-branch>. A disabled fieldset is
 * excluded from FormData AND from form validation, so the closed panel emits ZERO personalization
 * keys and only the active branch is validated. We only ever toggle `fieldset.disabled`.
 *
 * Rush: we reuse the existing <rush-personalization> component (assets/custom.js) for its checkbox,
 * label, price and the `properties[Rush]` hidden input. We never rebuild or reprice it. We resolve it
 * lazily at call time because a DOMContentLoaded mover relocates it inside the product form
 * (custom.js:320-346). We do NOT use its addRushProductToCart on this path: since R10 step 6 a rushed
 * add is ONE /cart/add carrying items:[garment, bare rush] (see onAtcCapture/atomicRushAdd), so no
 * cart snapshot can show the garment's `Rush: Yes` without the paid rush line or vice versa. That
 * closed the window the legacy positional sweep (custom.js removeRushProductFromCart, 1.5s after
 * every theme:cart:change) used to land in, and the settleRush/settleRushLoop polling reconciler that
 * contained it (commit ea6bdae) is deleted rather than carried past P5 (BUILD.md P5.2).
 */
if (!customElements.get('native-personalizer')) {
  // np-test-extract-start
  // Pure helpers for the R5 replace-on-add resolver. No DOM, no `this`, no fetch: the truth-table
  // test harness (docs/native-personalizer/np-truth-table-test.mjs, run it against this file's
  // path) extracts this block verbatim from the shipped file, so the decision logic is
  // provable offline. Why they exist: cart line keys on this store churn when the cart's
  // automatic-discount state changes (proven live 2026-07-20: byte-identical properties, new key,
  // after the third qualifying item activated BRIDAL-BUNDLE), so the armed key alone cannot
  // address the edited line. Safety invariant across every branch: the worst reachable failure is
  // a visible duplicate line, never a lost paid line.
  //
  // ONE deliberate exception, added 2026-07-30 (Carlin's call, DECISIONS D-R10.6): once the shopper
  // has followed the cart's edit link, the FIRST add on that form retires the edited line even if
  // they clicked "Remove personalization" first, and even if they then reopened the panel and
  // designed something different. Both are treated as the edit. The cost is that a shopper who
  // meant to keep the original AND add another unit of the same product loses the original, so on
  // this one path a paid line can go. Accepted because those clicks are indistinguishable from the
  // edit they usually are, and because the alternative is the reported duplicate defect
  // ($353.00 -> $557.00) staying reachable. Nothing OUTSIDE an edit-link visit can reach it: the
  // snapshot is armed only by initEditPrefill, against a validated cart line for this product.
  //
  // IN-PLACE EDITS (added 2026-10-10). The common edit (same variant, same selling plan, a
  // quantity-1 line, new design) no longer goes through add-then-decrement at all: it is ONE
  // /cart/change that rewrites the line's properties (npInPlaceEditTarget decides, npChangeLanded
  // verifies). Add-then-decrement could not serve it when the edited unit was the last in stock: the
  // theme's /cart/add answered 422 "already sold out" and the edit silently did nothing. The
  // resolver below (npResolveReplaceTarget) now serves only the edits that cannot be in place: a
  // size change, a qty>1 line, de-personalizing, and every fallback from the in-place path.

  // Canonicalise what the shopper typed into the exact characters that will be stitched.
  //
  // Why this exists: iOS "Smart Punctuation" is ON by default and silently substitutes typographic
  // code points for the ASCII ones the shopper actually typed. The SAME bride typing the SAME
  // keystroke gets U+0027 on a laptop and U+2019 on a phone. Nobody is choosing that; the device is.
  //
  // DIRECTION: U+2019 wins, not U+0027 (Carlin, 2026-07-31, DECISIONS D-R10.7). The two embroidery
  // faces are Times New Roman and Roundhand-bold (native-personalizer.css:228-235); on a cursive
  // script the straight U+0027 is a vertical typewriter tick that reads as a defect beside the
  // letterforms, while U+2019 is the typographically correct apostrophe for O'Brien and Bride's.
  // The open risk was whether the studio's digitising software carries the U+2019 glyph, because if
  // it does not then Bride's silently becomes Brides. CARLIN CONFIRMED IT DOES (2026-07-31), so the
  // direction is settled. Flipping it is still a one-line change to NP_APOSTROPHE if that ever
  // changes. The pattern in native-personalizer.liquid accepts all three single-quote forms
  // regardless, so a JS failure can never block a shopper typing the straight one.
  //
  // HARD INVARIANT, ENFORCED not assumed: this function never returns a string longer than the one
  // it was given. Violating it is a real over-length bug, because `maxlength` cannot catch it: the
  // spec raises validity.tooLong only for a value last changed by a USER edit, never a
  // script-assigned one, so a normaliser that grew an at-limit 18-character value would sail
  // through checkValidity() and reach the studio sheet over-length.
  //
  // The first version of this comment asserted the invariant held "because NFC only ever shrinks".
  // THAT IS FALSE and /code-review caught it. NFC EXPANDS 92 code points in the BMP-and-below
  // range, e.g. U+0344 COMBINING GREEK DIALYTIKA TONOS decomposes to U+0308 U+0301 (1 unit to 2),
  // and the result passes the shipped pattern. Pasting 18 of them into an 18-character field
  // produced a 36-character order line. The one-to-one character map was never the risk; NFC was.
  //
  // So the invariant is now a GUARD rather than a claim: if normalising grew the string, we return
  // the input untouched. Blunt on purpose (one pathological character forgoes canonicalisation of
  // the whole value) and safe: the input was user-typed and therefore already within maxlength, and
  // the pattern still judges it on its own merits. Harness n7 brute-forces every code point rather
  // than probing a hand-picked list, which is what let the original claim survive review.
  // Written as \u escapes, not literal glyphs: several of these are visually indistinguishable from
  // ASCII in most editors and terminals (that is the whole reason the bug exists), so a literal
  // would be unreviewable and one bad copy-paste would silently change behaviour.
  const NP_APOSTROPHE = '\u2019'; // RIGHT SINGLE QUOTATION MARK. The canonical form. Flip HERE.
  const NP_CHAR_MAP = {
    '\u0027': NP_APOSTROPHE, // APOSTROPHE, the ASCII typewriter tick
    '\u2018': NP_APOSTROPHE, // LEFT SINGLE QUOTATION MARK, the iOS opening quote
    '\u2013': '-', // EN DASH
    '\u2014': '-', // EM DASH
    '\u2026': '.' // HORIZONTAL ELLIPSIS. ONE char, never three: see the length invariant above.
  };
  const npNormalizeEmbroideryText = (value) => {
    if (typeof value !== 'string' || !value) return '';
    // Fail-open like the rest of this file: an engine without normalize() still gets the
    // substitutions, it just does not get the accent composition.
    const composed = typeof value.normalize === 'function' ? value.normalize('NFC') : value;
    let out = '';
    for (let i = 0; i < composed.length; i += 1) {
      const ch = composed.charAt(i);
      out += Object.prototype.hasOwnProperty.call(NP_CHAR_MAP, ch) ? NP_CHAR_MAP[ch] : ch;
    }
    // The guard. See the invariant above: NFC is not shrink-only.
    return out.length > value.length ? value : out;
  };

  // Both-direction key-by-key compare (never raw JSON.stringify: key order differs between
  // /cart.js reads). /cart.js serializes property values as strings, so String() is a no-op guard.
  const npPropsEqual = (a, b) => {
    const propsA = a || {};
    const propsB = b || {};
    const keys = Object.keys(propsA).concat(Object.keys(propsB));
    for (let i = 0; i < keys.length; i += 1) {
      const k = keys[i];
      const inA = Object.prototype.hasOwnProperty.call(propsA, k);
      const inB = Object.prototype.hasOwnProperty.call(propsB, k);
      if (inA !== inB) return false;
      if (String(propsA[k]) !== String(propsB[k])) return false;
    }
    return true;
  };

  const npSellingPlanId = (line) =>
    line && line.selling_plan_allocation && line.selling_plan_allocation.selling_plan
      ? String(line.selling_plan_allocation.selling_plan.id)
      : null;

  // A cart line's full identity: variant + selling plan + properties. The single definition used
  // by both the fingerprint filter and the changed-edit classification, so the two can not drift.
  const npIdentityMatches = (line, snapshot) =>
    String(line.variant_id) === String(snapshot.variantId) &&
    npSellingPlanId(line) === snapshot.sellingPlanId &&
    npPropsEqual(line.properties, snapshot.properties);

  // The add response identifies WHAT was just added only when it names exactly one line whose
  // properties are a plain object. A multi-item response is not authoritative (items[0] may not
  // be the garment), so it and every malformed shape return null = unclassifiable, which routes
  // the resolver to its conservative branch.
  const npClassifiableAddedLine = (response) => {
    if (!response || typeof response !== 'object') return null;
    let line = response;
    if (Object.prototype.hasOwnProperty.call(response, 'items')) {
      if (!Array.isArray(response.items) || response.items.length !== 1) return null;
      line = response.items[0];
    }
    if (!line || typeof line !== 'object' || Array.isArray(line)) return null;
    const props = line.properties;
    if (!props || typeof props !== 'object' || Array.isArray(props)) return null;
    return line;
  };

  // Find the cart line the armed snapshot refers to: by KEY first, else by identity FINGERPRINT
  // (variant + selling plan + full properties), which must match EXACTLY ONE line. Shared by the
  // replace-on-add resolver below and by the in-place edit gate (npInPlaceEditTarget), so the two
  // can never disagree about which line "the edited line" is. Returns { line, via } on a hit, or
  // { line: null, reason } when nothing is safely addressable (no match, or an ambiguous one).
  const npFindEditedLine = (cartItems, snapshot) => {
    const items = cartItems || [];
    const byKey = items.find((item) => item.key === snapshot.key);
    if (byKey) return { line: byKey, via: 'key' };
    const matches = items.filter((item) => npIdentityMatches(item, snapshot));
    if (matches.length !== 1) {
      return {
        line: null,
        reason:
          matches.length === 0
            ? 'original line not in cart and no fingerprint match'
            : 'fingerprint ambiguous (' + matches.length + ' matches)'
      };
    }
    return { line: matches[0], via: 'fingerprint' };
  };

  // Decide whether an edit can be applied IN PLACE: one /cart/change rewriting the edited line's
  // properties, instead of add-new-then-decrement-old. `garment` is the shape buildGarmentItem
  // returns: { id, quantity, properties, selling_plan? }. Returns { target: { key }, via, reason }
  // only when EVERY condition below holds, else { target: null, reason } naming the first that
  // failed (the caller then falls back to the add path, so a "no" here is always safe).
  //
  // Why these conditions (live probes on the real storefront, 2026-10-10):
  //   P1  /cart/change {id: key, quantity: 1, properties} on a stock-capped line returns 200 and a
  //       separate /cart.js read shows the new properties at qty 1, variant unchanged. The line key
  //       changes (key = variant:hash of properties).
  //   P2  `properties: {}` returns 200 but is a SILENT NO-OP (the old properties remain), and a
  //       non-empty object replaces the WHOLE set wholesale. Hence the empty-garment refusal here
  //       and npChangeLanded below as the independent check that the change actually applied.
  //   P3  changing a line's properties so it equals ANOTHER line's identity MERGES them (one line,
  //       quantity summed, new key). Identical to what the add-then-decrement path produces.
  //   P4  empty-string property values survive both /cart/add (FormData) and /cart/change (JSON).
  // Variant and selling plan must be UNCHANGED because /cart/change here only rewrites properties
  // (P1: variant unchanged; sending selling_plan is not needed and could alter the allocation).
  // Both quantities must be 1: a qty-2 line would need a split (one unit edited, one left), which
  // one /cart/change cannot express, and a garment qty above 1 is not a single-line edit.
  const npInPlaceEditTarget = (cartItems, snapshot, garment) => {
    const no = (reason) => ({ target: null, reason: reason });
    const snapProps = snapshot && snapshot.properties;
    if (!snapProps || typeof snapProps !== 'object' || Object.keys(snapProps).length === 0) {
      return no('snapshot has no properties');
    }
    if (!garment || !garment.properties || typeof garment.properties !== 'object' || Array.isArray(garment.properties)) {
      return no('garment has no usable properties');
    }
    if (Object.keys(garment.properties).length === 0) return no('empty property set would be a silent no-op');
    if (String(garment.id) !== String(snapshot.variantId)) return no('variant changed');
    if ((garment.selling_plan ? String(garment.selling_plan) : null) !== snapshot.sellingPlanId) {
      return no('selling plan changed');
    }
    if (garment.quantity !== 1) return no('garment quantity is not 1');
    const found = npFindEditedLine(cartItems, snapshot);
    if (!found.line) return no(found.reason);
    if (found.line.quantity !== 1) return no('edited line quantity is not 1');
    return { target: { key: found.line.key }, via: found.via, reason: 'in-place edit' };
  };

  // Did a /cart/change response actually apply the new design? Two conditions, both read from the
  // RESPONSE cart (the caller trusts it, never the status code, because a P2-class silent no-op is a
  // 200 that changed nothing):
  //   (a) some line carries the garment's variant + selling plan + properties. Quantity is
  //       deliberately NOT compared: after a P3 merge the landed line legitimately holds 2 or more.
  //   (b) when the edit CHANGED the properties, no line still carries the SNAPSHOT identity. Two
  //       lines with one identity cannot coexist (they merge, P3), so a surviving old-identity line
  //       after a changed edit means the change did not apply to it, even if (a) is satisfied by a
  //       different line that already held the new design (the false positive (a) alone allows).
  // An unchanged edit (garment properties equal the snapshot's) skips (b): old and new identity are
  // the same line. `snapshot` is optional only so callers without one keep check (a).
  const npChangeLanded = (cartItems, garment, snapshot) => {
    if (!garment) return false;
    const items = cartItems || [];
    const plan = garment.selling_plan ? String(garment.selling_plan) : null;
    const hasNewDesign = items.some(
      (item) =>
        String(item.variant_id) === String(garment.id) &&
        npSellingPlanId(item) === plan &&
        npPropsEqual(item.properties, garment.properties)
    );
    if (!hasNewDesign) return false;
    if (snapshot && !npPropsEqual(garment.properties, snapshot.properties)) {
      return !items.some((item) => npIdentityMatches(item, snapshot));
    }
    return true;
  };

  // Resolve which cart line the edit replacement should decrement. One decrement rule for BOTH
  // resolution paths (armed-key hit and fingerprint re-resolve):
  //   changed edit -> the target cannot be the just-added line (its identity differs from the
  //     snapshot by definition), so decrement even at quantity 1 (1 -> 0 removes it);
  //   unchanged or unclassifiable -> the target and the just-added line share one identity, so
  //     decrement only a real merge (quantity > 1); a lone quantity-1 line IS or may be the
  //     customer's just-paid item and must be left alone.
  // The added line's KEY is never consulted: it can itself be stale after discount churn.
  // Returns { key, quantity } to decrement, or a null target plus a reason to touch nothing.
  // `dePersonalized` is the caller asserting that this add STRIPPED personalization from the edited
  // line (the remove button was clicked and the panel was never reopened, so every property input
  // was disabled and the add posted none). It is needed because such an add is exactly the shape the
  // classifier above refuses: Shopify returns `properties: null` for a line with no properties
  // (fixture j6), so addedLine is null, editChanged would be false, and the quantity-1 guard would
  // block the decrement -- leaving the duplicate this whole path exists to prevent. Bypassing that
  // guard is safe here and ONLY here: both resolution routes address the snapshot's PERSONALIZED
  // identity (its key, or its full properties by fingerprint), and the just-added plain line matches
  // neither, so the target can never be the customer's just-added line.
  //
  // That claim depends on the snapshot actually carrying properties, so it is enforced HERE rather
  // than trusted from the caller. With an empty-properties snapshot the key route could select the
  // just-added plain line itself and delete it. initEditPrefill cannot currently arm such a snapshot
  // (it requires a non-empty embroidery/initials value), but the resolver is the layer the offline
  // harness proves, so it must not depend on a caller invariant three hundred lines away. An empty
  // snapshot also means there was no personalization to strip, so refusing the bypass is correct on
  // its own terms, not merely cautious; the classifier path below then applies unchanged.
  const npResolveReplaceTarget = (cartItems, snapshot, addedLine, dePersonalized) => {
    const found = npFindEditedLine(cartItems, snapshot);
    if (!found.line) return { target: null, reason: found.reason };
    const target = found.line;
    const via = found.via;
    const snapshotPersonalized = !!snapshot.properties && Object.keys(snapshot.properties).length > 0;
    const stripped = !!dePersonalized && snapshotPersonalized;
    const editChanged = stripped || (!!addedLine && !npIdentityMatches(addedLine, snapshot));
    if (!editChanged && target.quantity <= 1) {
      return { target: null, reason: 'sole identity match at quantity 1 may be the just-added line' };
    }
    return {
      target: { key: target.key, quantity: target.quantity },
      reason: stripped ? 'de-personalizing edit' : editChanged ? 'changed edit' : 'unchanged-edit merge',
      via: via
    };
  };

  // ONE per-branch descriptor table (MRS. Surname build, 2026-10-10). Every place that used to
  // hard-code "standard vs initials" or take the first `[data-np-text]` reads this instead, so a new
  // type is one row here (plus its fieldset in native-personalizer.liquid), not nine scattered
  // edits. Rows describe a branch's CONTROLS and its frozen `Personalization Type` value; they never
  // describe a cart property KEY (those stay in the markup, BUILD.md §3).
  //   input         selector for the branch's text input, resolved inside its fieldset
  //   textKey       the cart property that carries that text (also how a cart line is recognised)
  //   fontRadios    true when the shopper picks a font from radios (Standard)
  //   fixedFont     a FIXED `Embroidery font` value written from a hidden input (Surname), else null
  //   typeLabel     the exact `Personalization Type` value this branch writes (frozen)
  //   colorSelector selector for the branch's colour radios inside its fieldset, null = no colour
  //   mark          shows the "I heart" mark (Initials)
  //   lockup        stacks a fixed "MRS." line over the text (Surname)
  //   titleCase     the text is Title Cased on blur/confirm/add and previewed in that form (Surname only)
  //   uppercase     the text is UPPERCASED on blur/confirm/add and previewed in that form (Initials only)
  // `typeLabel` values are the frozen contract strings: never reword them.
  const NP_BRANCHES = {
    standard: {
      input: '[data-np-text]',
      textKey: 'Embroidery text',
      fontRadios: true,
      fixedFont: null,
      typeLabel: 'Standard Personalization',
      colorSelector: 'input[type="radio"][name="properties[Embroidery color]"]',
      mark: false,
      lockup: false
    },
    initials: {
      uppercase: true, // the posted initials are CAPITALS; previews show that form live (npUppercaseInitials)
      input: '[data-np-initials]',
      textKey: 'Initials',
      fontRadios: false,
      fixedFont: null,
      typeLabel: 'I ♥ (Initials)',
      colorSelector: null,
      mark: true,
      lockup: false
    },
    surname: {
      titleCase: true, // the posted name is Title Case; previews show that form live (npSurnameForm)
      input: '[data-np-surname]',
      textKey: 'Embroidery text',
      fontRadios: false,
      fixedFont: 'MRS. Surname',
      typeLabel: 'MRS. Surname',
      colorSelector: 'input[type="radio"][data-np-surname-color]',
      mark: false,
      lockup: true
    }
  };

  // `Personalization Type` value -> branch key, or null when it is not one of ours.
  const npBranchForType = (type) => {
    const keys = Object.keys(NP_BRANCHES);
    for (let i = 0; i < keys.length; i += 1) {
      if (NP_BRANCHES[keys[i]].typeLabel === type) return keys[i];
    }
    return null;
  };

  // Which branch a cart line belongs to, and whether it is a well-formed line for exactly ONE
  // branch. Shared by the standalone cart-edit prefill and alt-bundle.js's bundle prefill so the two
  // can never disagree. Returns {branch, text, font, color} or null (anything ambiguous or
  // out-of-contract: the caller aborts untouched, never guesses).
  //   - `Personalization Type` present  -> it names the branch (the only way to tell Standard from
  //     MRS. Surname, which both carry `Embroidery text`).
  //   - absent (single-type style)      -> today's inference: `Initials` = initials, else standard.
  // Key PRESENCE is judged by own-property, not truthiness: an empty-string key is out of contract.
  const npResolveEditLine = (props) => {
    const p = props || {};
    const has = (k) => Object.prototype.hasOwnProperty.call(p, k);
    const hasText = has('Embroidery text');
    const hasInitials = has('Initials');
    if (hasText === hasInitials) return null; // neither, or both (out of contract)
    let branch = null;
    if (has('Personalization Type')) {
      branch = npBranchForType(p['Personalization Type']);
      if (!branch) return null;
    } else {
      branch = hasInitials ? 'initials' : 'standard';
    }
    const desc = NP_BRANCHES[branch];
    if (!has(desc.textKey)) return null;
    if (desc.fontRadios) {
      if (!has('Embroidery font') || !has('Embroidery color')) return null;
    } else if (desc.fixedFont) {
      if (p['Embroidery font'] !== desc.fixedFont || !has('Embroidery color')) return null;
    } else if (has('Embroidery font') || has('Embroidery color')) {
      return null;
    }
    return { branch: branch, text: p[desc.textKey], font: p['Embroidery font'], color: p['Embroidery color'] };
  };

  // The value of the checked radio in a list, or null when none is checked. Pure (no DOM types, only
  // `.checked` / `.value`) so the harness can pin it. Used to derive the MRS. Surname hidden
  // `Embroidery color` from its display swatches.
  const npCheckedRadioValue = (radios) => {
    const list = radios || [];
    for (let i = 0; i < list.length; i += 1) {
      if (list[i] && list[i].checked) return list[i].value;
    }
    return null;
  };

  // MRS. SURNAME IS ALWAYS TITLE CASE (Carlin 2026-10-12: the studio and Impress stitch it that way).
  // Surname branch ONLY: Standard text and Initials stay exactly as typed. Segments are the runs between
  // spaces, hyphens and apostrophes (both U+0027 and the canonical U+2019; separators are kept as they
  // are). A segment that is all-lowercase or all-uppercase becomes first letter upper + the rest lower
  // ("BEAUMONT" -> "Beaumont", "D’ARCY" -> "D’Arcy", "van der berg" -> "Van Der Berg"). A MIXED-case
  // segment is the shopper's own capitalisation ("McDonald", "DeVries") and is kept, with only its first
  // letter forced upper ("mcDonald" -> "McDonald"). Case mapping is per character and Unicode-aware
  // ("élodie" -> "Élodie"); a character whose case mapping changes the length ("ß" -> "SS") is left as
  // typed, and the whole function returns its input untouched if the length would change at all, the
  // same hard invariant as npNormalizeEmbroideryText (counter and maxlength stay valid).
  const npTitleCaseSurname = (value) => {
    if (typeof value !== 'string' || !value) return '';
    const upper1 = (ch) => {
      const u = ch.toUpperCase();
      return u.length === ch.length ? u : ch;
    };
    const lower1 = (ch) => {
      const l = ch.toLowerCase();
      return l.length === ch.length ? l : ch;
    };
    const out = value
      .split(/([ \-'\u2019]+)/)
      .map((part, index) => {
        if (index % 2 === 1 || !part) return part; // a separator run (odd index) or empty
        const chars = Array.from(part);
        const uniform = part === part.toLowerCase() || part === part.toUpperCase();
        // Capitalise at the segment's FIRST LETTER, not at index 0: a segment with leading punctuation or a
        // digit ("(SMITH)", "&CO", "3m") must not lower-case wholesale because index 0 was a non-letter.
        const firstLetter = chars.findIndex((ch) => /\p{L}/u.test(ch));
        return chars.map((ch, i) => (firstLetter < 0 ? ch : i === firstLetter ? upper1(ch) : i > firstLetter && uniform ? lower1(ch) : ch)).join('');
      })
      .join('');
    return out.length === value.length ? out : value;
  };
  // INITIALS ARE ALWAYS CAPITALS (Carlin 2026-10-12), on every product with the initials type. Unicode-aware
  // ("é" -> "É"); a character whose uppercase form changes the length ("ß" -> "SS") is left as typed, and
  // the whole function returns its input untouched if the length would change at all (same hard invariant
  // as npNormalizeEmbroideryText: counter and maxlength stay valid). Initials input only.
  const npUppercaseInitials = (value) => {
    if (typeof value !== 'string' || !value) return '';
    const out = Array.from(value)
      .map((ch) => {
        const u = ch.toUpperCase();
        return u.length === ch.length ? u : ch;
      })
      .join('');
    return out.length === value.length ? out : value;
  };

  // What the surname field SHOULD display as the shopper types (apostrophes canonicalised, Title Case),
  // and the final posted form (also trimmed). The raw input is left alone until blur/confirm/add.
  const npSurnameForm = (value) => npTitleCaseSurname(npNormalizeEmbroideryText(value));
  const npSurnameFinal = (value) => npSurnameForm(value).trim();

  // A MRS. Surname value that already STARTS with a title as a word ("Mrs Smith", "MRS. Smith",
  // "Mrs.Smith", bare "mrs", also Mr/Ms/Miss/Mx/Dr): the lockup adds MRS. itself, so this would stitch
  // "MRS. Mr Smith". A positive lookahead for whitespace, a dot or end of string, NOT \b: \b is
  // ASCII-only, so it would treat the accented "Mrsé" as a boundary. Real surnames that merely start
  // with those letters ("Mrsic", "Mraz", "Msimang", "Drake", "Missoni") are followed by a letter and
  // are allowed.
  const NP_MRS_PREFIX = /^\s*(mrs|mr|ms|miss|mx|dr)(?=[\s.]|$)/i;
  // np-test-extract-end

  class NativePersonalizer extends HTMLElement {
    constructor() {
      super();
      // BUNDLE MODE (B2, added 2026-09-24). Set once, here, never re-derived: a personalizer does
      // not move between a bundle and a standalone PDP across its lifetime. Every bundle behaviour
      // in this file is gated on this being non-null; the corresponding else/standalone path is
      // unmodified pre-B2 code. See the "BUNDLE MODE (B2) public API" block near the end of the
      // class for the host contract (docs/native-personalizer/BUILD.md has no B2 section yet; the
      // contract lives in the B2 task brief until it's folded in).
      this.bundleScope = this.closest('[data-bundle-scope]');
      // `panel` now resolves to the CONFIRMED CARD, not the editable surface (drawer port,
      // 2026-08-31): onAtcCapture/onProductAdd key off `this.panel.hidden` as their "is anything
      // personalized" signal, so it has to mean "confirmed", not "the drawer happens to be open".
      // See the ALT-DRAWER region below for the full state-machine writeup.
      this.panel = this.querySelector('[data-np-panel]');
      this.openBtn = this.querySelector('[data-np-open]');
      this.removeBtn = this.querySelector('[data-np-remove]');
      this.drawer = this.querySelector('[data-np-drawer]');
      this.removedNote = this.querySelector('[data-np-removed-note]');
      this.confirmedRemoveBtn = this.querySelector('[data-np-confirmed-remove]');
      this.editBtn = this.querySelector('[data-np-edit]');
      this.drawerConfirmBtn = this.querySelector('[data-np-drawer-confirm]');
      this.drawerCancelBtn = this.querySelector('[data-np-drawer-cancel]');
      this.drawerCloseBtn = this.querySelector('[data-np-drawer-close]');
      // The pinned drawer preview card (visual-correction pass, 2026-08-31): a SINGLE shared
      // element both branches write into, matching alt-website-horizon's own design (JS never
      // checks which branch rendered it; only one branch's markup exists in the DOM at a time,
      // per the Liquid gate on `enabled_types contains 'standard'`).
      this.previewCard = this.querySelector('[data-np-preview-card]');
      this.previewCardText = this.querySelector('[data-np-preview-card-text]');
      this.previewCardMark = this.querySelector('[data-np-preview-card-mark]');
      this.ideasOpenBtn = this.querySelector('[data-np-ideas-open]');
      this.ideasDialog = this.querySelector('[data-np-ideas]');
      this.ideasCloseBtn = this.querySelector('[data-np-ideas-close]');
      this.lastConfirmed = null;
      this.drawerClosing = false;
      this.branchToggles = this.querySelectorAll('[data-np-branch-toggle]');
      this.fieldsets = this.querySelectorAll('fieldset[data-np-branch]');
      // One on-garment preview figure PER branch that carries a placement (Standard's, and since the
      // MRS. Surname build a second one for the surname lockup). Built into `this.ghosts` by
      // initGhostPreview(); `hasSurname` gates every surname-only behaviour so products without that
      // branch run exactly the code they ran before.
      this.ghostFigures = this.querySelectorAll('[data-np-ghost-preview]');
      this.ghosts = {};
      this.hasSurname = !!this.querySelector('fieldset[data-np-branch="surname"]');
      this._onProductAdd = this.onProductAdd.bind(this);
      this._onVariantChange = this.onVariantChange.bind(this);
      this._onAtcCapture = this.onAtcCapture.bind(this);
    // Bound (not inline closures) so disconnectedCallback can actually remove them: both attach to
    // the FORM / the ATC BUTTON, nodes this element does not own and can be moved away from.
    this._onFormSubmit = this.onFormSubmit.bind(this);
    this._onAtcClick = this.onAtcClick.bind(this);
    // Bundle mode only (B2): bound for the same reason as the two above, attaches to the
    // <bundle-component> ancestor, a node this element does not own either.
    this._onBundleVariantChange = this.onBundleVariantChange.bind(this);
    }

    connectedCallback() {
      // theme:product:add is a document-level listener; adding the same bound ref again is a no-op and
      // disconnectedCallback removes it, so attaching on every connect stays balanced. The ghost
      // preview's page-level hooks (window resize, fonts loadingdone, ResizeObserver) follow the
      // same pattern so a section re-render doesn't leak them.
      if (this.bundleScope) {
        // Bundle mode (B2, items 1-3): no document-level theme:product:add / theme:variant:change
        // listeners. The host dispatches `alt-bundle:variant-change` on the <bundle-component>
        // ancestor instead (never theme:variant:change, which many other document-level listeners
        // react to), and calls resetAfterBundleAdd() itself after a successful add rather than this
        // file ever seeing theme:product:add (see the BUNDLE MODE public API block below).
        this.bindBundleVariantListener();
        const available = this._boundBundleComponent
          ? this._boundBundleComponent.dataset.selectedVariantAvailable !== 'false'
          : true; // no <bundle-component> ancestor found: fail open, same direction as every other guard in this file
        this.applyAvailability(available);
      } else {
        document.addEventListener('theme:product:add', this._onProductAdd);

        // Sold-out toggle (2026-07-24): hide the whole personalizer when the selected variant can't be
        // bought, mirroring the Add to Cart button. Two parts, both balanced across (re)connects:
        //   1. A document-level theme:variant:change listener for live size switching after load.
        //   2. An immediate sync from the SERVER-RENDERED Add to Cart disabled state. The initial
        //      theme:variant:change fires during theme.js init, which is head-deferred and so runs
        //      BEFORE this body-deferred script defines the element — we would miss it. Reading the ATC
        //      button (already correct for the selected variant, incl. a ?variant=<sold-out> deep link
        //      and the bundle-parent-sold-out edge) is race-free.
        document.addEventListener('theme:variant:change', this._onVariantChange);
        const atcInit = this.atcButton();
        if (atcInit) this.applyAvailability(!atcInit.disabled);
      }

      if (this._ghostRender) {
        window.addEventListener('resize', this._ghostRender);
        if (document.fonts && document.fonts.addEventListener) {
          document.fonts.addEventListener('loadingdone', this._ghostRender);
        }
        if (this._ghostObserver) {
          Object.keys(this.ghosts).forEach((branch) => this._ghostObserver.observe(this.ghosts[branch].figure));
        }
      }

      // Atomic rush add (R10 step 6). CAPTURE phase on the FORM, which is an ancestor of the
      // add-to-cart button, so this runs BEFORE the button's own target-phase listener in the
      // theme's <product-form> (theme.js: it is that listener which dispatches `theme:cart:add`).
      // Stopping the event there suppresses `theme:cart:add`, and with it BOTH of its consumers in
      // one move: the theme's cartAddEvent (which would post the garment on its own) and custom.js's
      // rival 500ms addRushProductToCart (which would post the rush line separately). We own the add.
      //
      // That second suppression is load-bearing, contrary to the note at native-personalizer.liquid
      // :157-163, which says the panel's missing `[name="section-id"]` input makes custom.js:78 throw
      // and the legacy listener inert. VERIFIED FALSE on the QA theme 2026-07-29: the form carries a
      // section-id input of its own (from the theme's form, not the panel), it matches the rush
      // element's parentId exactly, so the legacy listener was live and racing us on every rushed add.
      // Leave that liquid comment to be corrected with the rest of the P5.2 teardown; do not rely on it.
      //
      // Blast radius, stated honestly: stopPropagation on a rushed add stops this click reaching
      // EVERY document-level listener, not just the two theme:cart:add consumers — any app embed or
      // DOM-based analytics counting add-to-cart clicks will miss rushed adds. Accepted: the theme's
      // own analytics ride the cart events we still dispatch, and the alternative (letting the click
      // through) is the two-call flow this change exists to remove. The one piece of theme state that
      // listener also set, window.a11y.lastElement, is restored by hand in onAtcCapture.
      // The sticky cart bar needs no separate hook: product.js implements it by dispatching a
      // synthetic BUBBLING click on this same button, so it passes through this listener too.
      //
      // DELIBERATELY OUTSIDE the `_wired` once-guard below (moved 2026-07-31, /code-review triage).
      // It is the only child listener that must be BALANCED across connect/disconnect rather than
      // wired once, and the two constraints pull in opposite directions:
      //   - Leaving it unremoved leaks a listener onto a detached form. Benign today only because
      //     onAtcCapture self-neutralises (detached ⇒ this.form null ⇒ atcButton() null ⇒ returns).
      //   - But removing it in disconnectedCallback while `_wired` stays true means a RECONNECT
      //     (section re-render, DOM move) never re-adds it, and every rushed add then silently falls
      //     back to the theme + custom.js two-call race that R10 step 6 exists to eliminate. That is
      //     a money bug traded for a harmless leak.
      // So: re-resolve the form and rebind on every connect, remove from the SAME recorded node on
      // disconnect (`this.form` is this.closest('form') and is null once detached, so the reference
      // has to be stored). Re-adding an identical (type, listener, capture) triple is a spec no-op,
      // so a connect without an intervening disconnect cannot stack duplicates. Do NOT solve this by
      // resetting `_wired`: that would re-stack the fresh-closure listeners it exists to guard.
      // The submit and ATC-click listeners are bound here too, for the same reason and by the same
      // bookkeeping (extended 2026-07-31 after Codex). They attach to the FORM and to the ATC
      // BUTTON, neither of which this element owns, so leaving them in the `_wired` once-guard left
      // them pinned to the node resolved at FIRST connect: after a move they fire against a stale
      // form while the live one has no listener. Not cosmetic now that the submit handler carries
      // the Shop Pay canonicalisation.
      // Bundle mode (B2, item 1): no bindFormListeners() at all, i.e. no form submit / ATC capture /
      // ATC click listeners. The bundle form has no [data-add-to-cart] button by contract (the host
      // owns its own add-to-cart flow entirely; see the BUNDLE MODE public API block below), so
      // every one of those three listeners would either bind to nothing useful or, worse, sit on
      // the bundle form ready to fire against a button that isn't there.
      if (!this.bundleScope) this.bindFormListeners();

      // Child/self listeners are fresh closures. Guard against a reconnect (section re-render, DOM move)
      // stacking duplicates, which would fire the ATC click twice on Enter. Wire them once per instance.
      if (this._wired) return;
      this._wired = true;

      // A USER-initiated open permanently retires any pending edit prefill (the prefill's own
      // apply path calls openDrawer() directly, never this button): once the shopper starts driving,
      // a late-resolving prefill must not reopen or repopulate the panel, even if they close it
      // again before the prefill's /cart.js read returns. Same for add-to-cart initiation, handled
      // by onAtcClick / onFormSubmit above: invalidating only when theme:product:add fires (the add
      // RESPONSE) would leave a window where a pending prefill applies mid-add and mis-attributes
      // that unrelated add as the edit.
      //
      // It does NOT retire an ARMED _npEditSnapshot, and that is deliberate (Carlin, 2026-07-30).
      // The reachable sequence is: edit link -> "Remove personalization" (which keeps the snapshot
      // armed) -> shopper reopens here and personalizes differently -> add. That shopper arrived to
      // edit ONE cart line and is still editing it, so the add replaces that line rather than
      // standing beside it. Dropping the snapshot here would leave the reported duplicate defect
      // ($353 -> $557) reachable by this second route. The accepted cost: a shopper who genuinely
      // wanted a SECOND personalized item of the same product ends up with one, which is the only
      // place in this file where a replace can retire a line the shopper meant to keep. Chosen over
      // the duplicate because arriving via the edit link is an unambiguous edit intent.
      if (this.openBtn) {
        this.openBtn.addEventListener('click', () => {
          this._npPrefillInvalid = true;
          this.openDrawer();
          // Land the cursor in the text field (Carlin 2026-09-25: the trigger reads as a field, so
          // the tap that opens the drawer should already be "in" it). Synchronous, inside this
          // click, on purpose: iOS Safari only raises the keyboard for a focus() made within the
          // user gesture. Trigger only, not the pencil: a re-edit is as often a font/colour change.
          this.focusActiveInput();
        });
      }
      // keepEditSnapshot: removing a personalization during an edit session IS the edit, so the
      // armed replacement has to survive this close and be consumed by the plain add that follows.
      // See close() and onProductAdd for the invariant that makes hidden-panel + armed unambiguous.
      // Both entry points (the in-drawer link and the confirmed card's ×) route through the same
      // removePersonalization(), so this invariant only has to be stated once.
      if (this.removeBtn) this.removeBtn.addEventListener('click', () => this.removePersonalization());
      if (this.confirmedRemoveBtn) this.confirmedRemoveBtn.addEventListener('click', () => this.removePersonalization());

      // ALT-DRAWER: wiring for the ported slide-out drawer. All of these are DESCENDANTS of this
      // element (the dialog, its header/footer buttons, the confirmed card's pencil), unlike the
      // form/ATC-button listeners above, so they belong in this once-per-instance `_wired` guard,
      // not the bind-and-rebalance dance bindFormListeners()/unbindFormListeners() use for nodes
      // this element does not own.
      if (this.editBtn) {
        this.editBtn.addEventListener('click', () => {
          this._npPrefillInvalid = true;
          this.openDrawer();
        });
      }
      if (this.drawerConfirmBtn) this.drawerConfirmBtn.addEventListener('click', () => this.confirmDrawer());
      if (this.drawerCancelBtn) this.drawerCancelBtn.addEventListener('click', () => this.revertDrawerEdits());
      if (this.drawerCloseBtn) this.drawerCloseBtn.addEventListener('click', () => this.revertDrawerEdits());
      if (this.drawer) {
        // Esc fires the dialog's native `cancel` event; preventDefault it so the browser's own
        // instant close can't pre-empt the exit animation, and route it through the same revert
        // path as every other dismissal (Broadcast's own quick-add modal does the identical thing
        // for its Escape handler, theme.dev.js:7167-7172).
        this.drawer.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.revertDrawerEdits();
        });
        // Backdrop click: the drawer fills its own box entirely (no padding), so a click whose
        // target is the <dialog> itself can only have landed on the ::backdrop (same reasoning as
        // the ghost-modal's own backdrop-close above).
        this.drawer.addEventListener('click', (event) => {
          if (event.target === this.drawer) this.revertDrawerEdits();
        });
        // Two-phase close, mirroring Broadcast's own quick-add modal idiom (theme.dev.js:7109-7192):
        // slideDrawerClosed() sets [closing] and returns; this animationend listener calls it again,
        // which then performs the real .close(). A dialog's own CSS animation is what drives this,
        // same generic mechanism the theme already relies on for its quick-add modal.
        this.drawer.addEventListener('animationend', (event) => {
          if (event.target !== this.drawer) return;
          if (this.drawer.hasAttribute('closing')) this.slideDrawerClosed();
        });
      }
      // Both branches' text inputs feed the SAME pinned preview card; wired below alongside the
      // existing counter/charset listeners on the shared `input[maxlength]` loop, and also kept
      // in sync with the active branch by enableBranch() itself (called on every toggle/open/
      // restore), so updatePreviewCard() never has to be called from more than those two sites.

      // Ideas popup (ported from alt-website-horizon): display-only examples, no tap-to-fill.
      // showModal(), same as the ghost-modal, for top-layer + inert-page + native Esc/focus
      // handling for free. Backdrop-click-to-close mirrors the ghost-modal's own pattern: the
      // dialog's own padding is zero and its inner column is centred, so a click landing on the
      // dialog element itself (not a descendant) can only have been the backdrop.
      if (this.ideasOpenBtn && this.ideasDialog) {
        this.ideasOpenBtn.addEventListener('click', () => this.ideasDialog.showModal());
      }
      if (this.ideasCloseBtn && this.ideasDialog) {
        this.ideasCloseBtn.addEventListener('click', () => this.ideasDialog.close());
      }
      if (this.ideasDialog) {
        this.ideasDialog.addEventListener('click', (event) => {
          if (event.target === this.ideasDialog) this.ideasDialog.close();
        });
      }

      this.branchToggles.forEach((toggle) => {
        toggle.addEventListener('change', () => {
          if (toggle.checked) this.enableBranch(toggle.value);
        });
      });

      this.querySelectorAll('[data-np-font]').forEach((radio) => {
        if (radio.checked) this.applyFont(radio.dataset.npFont);
        // change only ever fires on the radio that just became checked, same as the thread-color
        // radios below; the seed call above covers the default-checked one on load.
        radio.addEventListener('change', () => this.applyFont(radio.dataset.npFont));
      });

      // Display-only mirror of the checked thread colour's name (the input is a hidden radio behind
      // a bare swatch, so on touch there is no other way to read the selection). The span carries no
      // name= attribute: it can never reach FormData.
      // Each branch with a colour row owns its own name mirror (looked up from the radio's fieldset).
      // The MRS. Surname branch's swatches are DISPLAY radios (their own group name, see the Liquid
      // comment) and its submitted `Embroidery color` is a hidden input synced here on every change.
      const colorRadios = this.querySelectorAll(
        'input[type="radio"][name="properties[Embroidery color]"], input[type="radio"][data-np-surname-color]'
      );
      // ALSO sets --np-thread-color on the pinned preview card (visual-correction pass,
      // 2026-08-31), matching alt-website-horizon's showColorName(): the preview text reads in
      // the actual selected thread colour, not a neutral ink, with a relief text-shadow for pale
      // threads (native-personalizer.css). removeProperty(), not set to '', when a radio carries
      // no data-np-hex (a config with no ghost preview and no per-swatch hex data at all): a CSS
      // custom property does not self-clear the way textContent does.
      const applyThreadColor = (radio) => {
        const radioFieldset = radio.closest('fieldset');
        const colorName = (radioFieldset || this).querySelector('[data-np-color-name]');
        if (colorName) colorName.textContent = radio.value;
        if (radio.hasAttribute('data-np-surname-color') && radioFieldset) {
          const surnameColorValue = radioFieldset.querySelector('[data-np-surname-color-value]');
          if (surnameColorValue) surnameColorValue.value = radio.value;
        }
        if (!this.previewCard) return;
        if (radio.dataset.npHex) this.previewCard.style.setProperty('--np-thread-color', radio.dataset.npHex);
        else this.previewCard.style.removeProperty('--np-thread-color');
      };
      colorRadios.forEach((radio) => {
        // On a surname product TWO radios are checked by default (one per branch) and both are applied
        // here, the last (surname's) winning. Harmless only because both palettes come from the same
        // metaobject array, so the two defaults are the same first colour.
        if (radio.checked) applyThreadColor(radio);
        // change only ever fires on the radio that just became checked; no guard needed.
        radio.addEventListener('change', () => applyThreadColor(radio));
      });
      // Seed the hidden surname colour from whatever display radio is checked right now (see
      // syncSurnameColorValue for why a `change` listener alone is not enough).
      this.syncSurnameColorValue();

      this.querySelectorAll('input[maxlength]').forEach((input) => {
        input.addEventListener('input', () => {
          this.updateCounter(input);
          this.refreshCharsetValidity(input);
          this.updatePreviewCard();
        });
        // Canonicalise on blur so the shopper SEES the characters that will be stitched. Not on
        // every keystroke: rewriting mid-word fights the caret and, on iOS, the substitution has
        // not even happened yet while the word is still being composed.
        input.addEventListener('blur', () => this.normalizeField(input));
        this.updateCounter(input);
        this.refreshCharsetValidity(input);
      });

      // Enter inside a panel input must NOT implicitly submit the form. An implicit submit does a
      // full-page POST /cart/add: properties survive but `theme:product:add` never fires, the rush
      // line never adds, and the 1.5s cleanup then strips `Rush: Yes` (find #24). Intercept it and
      // click the real add-to-cart button so the normal AJAX path runs.
      //
      // ALT-DRAWER addition: while the drawer is open, Enter CONFIRMS instead (same UX proven on
      // alt-website-horizon, drawer build 2026-08-27, reviewed and accepted) rather than forwarding
      // to the add-to-cart button, the drawer's own fieldset is enabled at this point, so an
      // implicit-submit risk still exists and confirming is the correct resolution, not add-to-cart.
      // The closed-drawer branch below is unchanged: it's what prevents the implicit full-page POST.
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || !event.target.matches('input')) return;
        // Horizon F1(a) fix, ported (Codex-caught: this file's own pre-existing Enter interception
        // never had this guard either, but touching this block is a cheap place to close it now).
        // An Enter that fires while an IME is mid-composition is confirming the CANDIDATE TEXT, not
        // asking to submit or confirm the field, hijacking it here would cut a composition short.
        if (event.isComposing) return;
        // `!this.drawerClosing` (code-review-caught, 2026-08-31): matches the click-gate's own
        // condition below. Without it, an Enter pressed again during the post-confirm exit
        // animation (drawer.open stays true that whole time) routes to confirmDrawer(), which
        // no-ops on its own `drawerClosing` guard, swallowing the keystroke instead of falling
        // through to the add-to-cart click a shopper pressing Enter again would reasonably expect.
        if (this.drawer && this.drawer.open && !this.drawerClosing) {
          event.preventDefault();
          this.confirmDrawer();
          return;
        }
        event.preventDefault();
        const button = this.atcButton();
        if (button) button.click();
      });

      this.initGhostPreview();

      // R5 cart edit-link prefill. Fire-and-forget: every failure path leaves plain v1 PDP behavior.
      // Bundle mode (B8, added 2026-09-24): the host (<alt-bundle-product>, assets/alt-bundle.js)
      // owns the ENTIRE bundle edit-from-cart flow (fingerprint line resolution, opening the right
      // accordion tile, prefill, save via /cart/change.js). This standalone-only prefill must not
      // ALSO run against the same np_edit param in bundle mode: (a) a tile whose accordion panel
      // starts closed renders `[hidden]`, and this.openDrawer()'s showModal() throws against a
      // dialog that isn't being rendered, not merely present in the DOM; (b) even the initially-open
      // tile would race the host's own prefill, since both independently await /cart.js and both
      // call openDrawer(). Standalone behaviour (the `else` implied by this guard) is unchanged.
      if (!this.bundleScope) this.initEditPrefill();
    }

    // Bind every listener that lives on a node this element does NOT own: the form (capture-phase
    // add gate + submit) and the add-to-cart button. Re-resolved on every connect and balanced by
    // unbindFormListeners, because the element can be moved under a different form. Re-adding an
    // identical (type, listener, capture) triple is a spec no-op, so a connect with no intervening
    // disconnect cannot stack duplicates.
    bindFormListeners() {
      const form = this.form;
      const atc = this.atcButton();
      if (form !== this._boundForm || atc !== this._boundAtc) this.unbindFormListeners();
      if (form) {
        form.addEventListener('click', this._onAtcCapture, true);
        form.addEventListener('submit', this._onFormSubmit);
        this._boundForm = form;
      }
      if (atc) {
        atc.addEventListener('click', this._onAtcClick);
        this._boundAtc = atc;
      }
    }

    // Removes from the RECORDED nodes, never from this.form / this.atcButton(): those resolve via
    // this.closest('form') and already return null once the element is detached.
    unbindFormListeners() {
      if (this._boundForm) {
        this._boundForm.removeEventListener('click', this._onAtcCapture, true);
        this._boundForm.removeEventListener('submit', this._onFormSubmit);
      }
      if (this._boundAtc) this._boundAtc.removeEventListener('click', this._onAtcClick);
    }

    // Bundle mode only (B2, item 2). Mirrors bindFormListeners()/unbindFormListeners() above for a
    // node this element does not own: re-resolved on every connect (a reconnect under a different
    // <bundle-component>, e.g. a section re-render, rebinds correctly), removed from the RECORDED
    // node on disconnect/rebind rather than a fresh closest() call, same reasoning as that pair.
    // Re-adding an identical (type, listener) pair is a spec no-op, so a connect with no
    // intervening disconnect cannot stack duplicates.
    bindBundleVariantListener() {
      const bundleComponent = this.closest('bundle-component');
      if (bundleComponent !== this._boundBundleComponent) this.unbindBundleVariantListener();
      if (bundleComponent) {
        bundleComponent.addEventListener('alt-bundle:variant-change', this._onBundleVariantChange);
        this._boundBundleComponent = bundleComponent;
      }
    }

    unbindBundleVariantListener() {
      if (this._boundBundleComponent) {
        this._boundBundleComponent.removeEventListener('alt-bundle:variant-change', this._onBundleVariantChange);
      }
    }

    // Bundle mode's replacement for onVariantChange() (item 2): the host dispatches this on the
    // <bundle-component> itself (bubbles), detail.variant = the Shopify variant JSON with
    // `.available`, exactly like theme:variant:change's own detail shape, so the same
    // `!!(variant && variant.available)` reduction applies unchanged.
    onBundleVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      this.applyAvailability(!!(variant && variant.available));
    }

    onFormSubmit() {
      this._npPrefillInvalid = true;
      // Second canonicalisation site, for form submits that never reach onAtcCapture. The capture
      // gate keys on [data-add-to-cart], but product-buttons.liquid:266 can render
      // {{ form | payment_button }} into this same form, and Shop Pay / Apple Pay submit it without
      // ever clicking that button, so the raw device-dependent value would go straight to the
      // order. Latent today (show_payment_button is false on all nine product templates), but a
      // merchant toggling it in the editor must not silently reintroduce the very inconsistency
      // this change exists to remove. A native submit serialises AFTER this handler runs, so
      // rewriting values here does reach the POST. Residual gap, stated honestly: a wallet button
      // that intercepts and posts its own payload instead of submitting the form bypasses this too.
      this.normalizeAllFields();
    }

    onAtcClick() {
      this._npPrefillInvalid = true;
    }

    disconnectedCallback() {
      if (this.bundleScope) {
        this.unbindBundleVariantListener();
      } else {
        document.removeEventListener('theme:product:add', this._onProductAdd);
        document.removeEventListener('theme:variant:change', this._onVariantChange);
      }
      // Balanced against bindFormListeners() in connectedCallback. The recorded nodes are
      // deliberately NOT nulled: a reconnect onto the same form then compares equal and simply
      // re-adds (a spec no-op if the listener were somehow still attached), while a reconnect under
      // a DIFFERENT form detaches the old one first.
      this.unbindFormListeners();
      // Disarm the atomic-add garment id on the way out (Codex, 2026-07-31). atomicRushAdd sets it
      // BEFORE its fetch await and relies on onProductAdd to consume it, but the line above has
      // just removed the theme:product:add listener, so a response that lands after the element
      // disconnects can never reach onProductAdd and the id would stay armed for the next instance
      // to mis-classify against. Clearing here is safe rather than lossy: if we are detached there
      // is no onProductAdd to serve, and a later add with no stashed id simply routes
      // addedGarmentLine to its conservative branch (a visible duplicate at worst, never a lost
      // paid line), which is the documented safe failure direction for this whole file.
      this._npAtomicGarmentVariantId = null;
      // ALT-DRAWER (code-review-caught): both drawer timers must die with the instance, or they
      // fire later against a detached element (a section re-render, a quickview swap).
      clearTimeout(this._drawerCloseBackstop);
      clearTimeout(this._removedNoteTimer);
      if (this._ghostRender) {
        window.removeEventListener('resize', this._ghostRender);
        if (document.fonts && document.fonts.removeEventListener) {
          document.fonts.removeEventListener('loadingdone', this._ghostRender);
        }
        if (this._ghostObserver) this._ghostObserver.disconnect();
      }
    }

    get form() {
      return this.closest('form');
    }

    atcButton() {
      return this.form ? this.form.querySelector('[data-add-to-cart]') : null;
    }

    // theme:variant:change is document-level and bubbles from every product-form on the page. Only
    // react to OUR product: the event target is this product's <product-component>
    // ([data-section-type]) container, a DOM ancestor of this element; contains(this) is false for a
    // featured-product section or a quickview modal of another product.
    onVariantChange(event) {
      if (!event.target || !event.target.contains(this)) return;
      const variant = event.detail && event.detail.variant;
      this.applyAvailability(!!(variant && variant.available));
    }

    // Show/hide the whole personalizer with the buy button. Hiding is not merely visual: while hidden
    // for sold-out, every branch fieldset is disabled so ZERO personalization keys can serialize on an
    // unbuyable variant, even via a non-AJAX submit path (a disabled fieldset is excluded from
    // FormData). Field VALUES are preserved, so re-showing an open panel restores the shopper's input.
    applyAvailability(available) {
      if (available === this._npAvailable) return; // idempotent: theme fires on every option change
      // ALT-DRAWER: a <dialog> opened via showModal() renders in the browser's top layer, so
      // setting `hidden` on this ancestor element (below) does not reliably un-render it, it would
      // leave an invisible, still-modal dialog trapping the whole PDP inert behind it (the same
      // top-layer/display:none interaction this file's own ghost-modal comment already documents,
      // native-personalizer.liquid). Force-close BEFORE the hide, not after: ordering is what
      // prevents this, not a new mechanism.
      //
      // Codex-caught (2026-08-31): if a CONFIRMED design was being re-edited into something new
      // when this fires, force-closing without restoring first would leave the (still enabled)
      // fieldset holding that unconfirmed draft while panel.hidden correctly stays false. Then,
      // when availability RETURNS, the branch below re-arms `activeBranch()` and the stale draft
      // (never confirmed) becomes what the next add posts. Restore the snapshot first, same as
      // revertDrawerEdits()'s own prior-confirmed branch. Nothing to restore when there was no prior
      // confirm: the unconditional fieldset-disable a few lines below already neutralises that
      // first-time-editing draft. Routed through slideDrawerClosed() (not a bare .close()) so the
      // 400ms backstop timer from any earlier close attempt is cleared too, not just this one's own
      // state, an uncleared timer could otherwise fire against a drawer reopened after this point.
      if (this.drawer && this.drawer.open) {
        if (this.lastConfirmed) this.restoreSnapshot(this.lastConfirmed);
        this.slideDrawerClosed();
      }
      // Code-review-caught (2026-08-31): the ghost full-render popup is its OWN <dialog>, opened
      // via showModal() from inside the drawer, so it can be open on top of everything above when
      // this fires. Left unclosed it would be the exact "invisible-but-still-modal dialog trapping
      // the page" failure this whole guard exists to prevent, just one layer deeper.
      Object.keys(this.ghosts).forEach((branch) => {
        const modal = this.ghosts[branch].modal;
        if (modal && modal.open) modal.close();
      });
      this._npAvailable = available;
      this.hidden = !available;
      if (!available) {
        this.fieldsets.forEach((fs) => {
          fs.disabled = true;
        });
      } else if (this.panel && !this.panel.hidden) {
        // A personalization was CONFIRMED when it went sold-out; re-arm the branch the shopper was
        // using so its values keep serializing into FormData once available again. An unconfirmed
        // panel stays pristine (all branches disabled), matching close()'s invariant.
        //
        // ALT-DRAWER, behavior change from the pre-drawer flow (code-review-caught, 2026-08-31): a
        // shopper mid-way through a FIRST-TIME (never confirmed) edit when this fires no longer has
        // that draft auto-resume when availability returns, the drawer force-closed above and this
        // branch only re-arms a CONFIRMED design. The draft itself is not lost (values stay in the
        // DOM, just disabled); re-clicking the trigger reopens the drawer with it still there. This
        // is a deliberate trade-off, not an oversight: auto-reopening a MODAL dialog unprompted when
        // availability returns (to replicate the old inline-panel's auto-resume) would itself be
        // poor practice (showModal() with no direct user gesture, stealing focus unexpectedly).
        this.enableBranch(this.activeBranch());
      }
      // Bundle mode (B2, item 7): applyAvailability() otherwise never touches rush at all. A
      // sold-out/back-in-stock flip on ANY component can change whether the bundle's ONE shared
      // rush control should be showing (this component may have just been the one confirmed
      // component, or the one that just came back in stock), so bundle mode re-evaluates it here
      // too. syncBundleRush() is a no-op when bundleScope is unset, so this line changes nothing
      // in standalone.
      if (this.bundleScope) this.syncBundleRush();
    }

    // Resolve lazily every time: the element is moved into the form after DOMContentLoaded.
    // Bundle mode (B2, item 4): scoped to THIS bundle, never the page-wide element, so a bundle
    // page carrying more than one <alt-bundle-product> (not expected today, but nothing enforces
    // it) can't cross-wire one bundle's rush control to another's personalizers.
    get rush() {
      if (this.bundleScope) return this.bundleScope.querySelector('rush-personalization');
      return document.querySelector('rush-personalization');
    }

    // The rush checkbox, resolved from the DOM every time.
    //
    // NEVER read custom.js's `rush.checkbox` instead. That property is assigned once in the
    // constructor via this.querySelector (custom.js:75), so an element upgraded before its children
    // were parsed carries null for life. Every read of it is a latent silent-skip, which is the bug
    // class resolveRushControl was introduced for and which /code-review found still live in
    // onAtcCapture on 2026-07-30. Reading the DOM costs nothing and cannot go stale.
    //
    // Use this where the answer is needed synchronously. Use resolveRushControl where waiting is
    // possible and a miss must fail closed (the edit prefill).
    get rushCheckbox() {
      const rush = this.rush;
      return rush ? rush.querySelector('input[type=checkbox]') : null;
    }

    // Rush moved to the bag (2026-09-30): snippets/rush-personalization.liquid renders the element
    // DORMANT (`data-rush-dormant`). It stays in the DOM for frozen custom.js, but no path here may
    // tick it, stamp `Rush: Yes` or add the Rush line. The Rush line in the cart is order-level and
    // is left alone by every path below.
    get rushDormant() {
      const rush = this.rush;
      return !!(rush && rush.hasAttribute('data-rush-dormant'));
    }

    // Bounded wait for a USABLE rush control: the element present and upgraded, plus its checkbox.
    // Returns {element, checkbox} or null. Never throws and never hangs; the caller fails closed.
    //
    // We resolve the checkbox by querying the element rather than reading custom.js's `rush.checkbox`,
    // because that property is assigned once in the constructor via this.querySelector and is null
    // forever on an element upgraded before its children were parsed. `updateProductProperty()` and
    // `show()` both read `this.form`, never `this.checkbox`, so an upgraded element is enough for
    // them. Deliberately does NOT write to rush.checkbox: custom.js is byte-frozen and repairing
    // another component's internals is not this file's business.
    //
    // Polls rather than observing, because the reason it is late is a MOVE (see the getter above).
    //
    // COST, by budget rather than by guess (2026-07-29; Codex raised the magnitude, /code-review
    // corrected the attribution). Already usable → returns in ~0ms. Otherwise the budget can be spent
    // TWICE, because whenDefined is raced against its own timeoutMs and polling then starts a fresh
    // deadline. Which of the two legs actually costs anything depends on WHY it is not usable:
    //   - class already registered, element merely late (the documented MOVE above, and the 1-in-5
    //     miss at :1083): whenDefined settles in a microtask, so only polling runs → ≤~1s.
    //   - definition missing or registering late (custom.js blocked or slow): both legs run → ~2s.
    // Do not read the ~0ms as "nearly every shopper": this is called from exactly one place, the edit
    // prefill, and within that population the measured miss rate was 1 in 5.
    //
    // Honest gap: the forced tests in QA-LOG measured 2055ms (fail closed) and 2009ms (recover) with
    // the class already registered, which the reading above does not fully account for. The bound and
    // both OUTCOMES are verified; the decomposition is not.
    //
    // A single-shared-deadline tightening was built, QA'd and REVERTED: under it an element
    // re-inserted at 300ms went unseen inside a 1000ms budget. Unexplained, and an unexplained change
    // on a money path is worse than a slow abort path. Do not retry it without re-running both forced
    // tests. The slow path is the one that used to fail SILENTLY; slower and correct wins here.
    async resolveRushControl(timeoutMs = 1000) {
      // `elementSeen` records whether a <rush-personalization> was EVER observed during the wait,
      // and it is what the caller's fail-closed guard must key off (2026-07-31, /code-review
      // triage). The guard used to re-read `this.rush` after this await, a fresh live query that can
      // disagree with everything the waiter actually saw. Deliberately NOT solved by snapshotting
      // `this.rush` before the wait either: that reintroduces exactly the staleness this waiter
      // exists to absorb, because the element can arrive late via the DOMContentLoaded move that
      // the `rush` getter documents, and it would make the guard wrongly PASS on a page that does
      // have one.
      let elementSeen = false;
      const usable = () => {
        const element = this.rush;
        if (!element) return null;
        elementSeen = true;
        if (typeof element.updateProductProperty !== 'function') return null;
        const checkbox = element.querySelector('input[type=checkbox]');
        return checkbox ? { element: element, checkbox: checkbox } : null;
      };

      // Every return below reports elementSeen alongside the control, so the caller decides the
      // exemption from ONE observation instead of a post-await re-query.
      let found = usable();
      if (found) return { control: found, elementSeen: elementSeen };

      // Upgrade is the common cause and whenDefined settles as soon as the class is registered,
      // which is usually well before the timeout. Race it so a missing definition cannot hang us.
      try {
        if (window.customElements && typeof customElements.whenDefined === 'function') {
          await Promise.race([
            customElements.whenDefined('rush-personalization'),
            new Promise((resolve) => setTimeout(resolve, timeoutMs))
          ]);
        }
      } catch (error) {
        // whenDefined only rejects on an invalid name; fall through to polling regardless.
      }

      const deadline = Date.now() + timeoutMs;
      found = usable();
      while (!found && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        found = usable();
      }
      return { control: found || null, elementSeen: elementSeen };
    }

    activeBranch() {
      const checked = Array.prototype.find.call(this.branchToggles, (t) => t.checked);
      if (checked) return checked.value;
      return this.fieldsets.length ? this.fieldsets[0].dataset.npBranch : null;
    }

    // Branch-descriptor resolvers (NP_BRANCHES above): the ONE way the rest of this file finds a
    // branch's fieldset, text input and colour radios, so no call site hard-codes standard vs
    // initials vs surname.
    branchDesc(branch) {
      return Object.prototype.hasOwnProperty.call(NP_BRANCHES, branch) ? NP_BRANCHES[branch] : null;
    }

    branchFieldset(branch) {
      return branch ? this.querySelector('fieldset[data-np-branch="' + branch + '"]') : null;
    }

    // The branch's text input, resolved inside its own fieldset (never a bare first-match query:
    // two branches both write `Embroidery text`).
    branchInput(branch) {
      const desc = this.branchDesc(branch);
      const fieldset = this.branchFieldset(branch);
      return desc && fieldset ? fieldset.querySelector(desc.input) : null;
    }

    // The text a branch's PREVIEWS show for a raw input value: the Title Case form for MRS. Surname and the
    // UPPERCASE form for Initials (the forms that will be posted), the raw value for Standard. Display only; never written back.
    branchDisplayValue(branch, value) {
      const desc = this.branchDesc(branch);
      if (desc && desc.titleCase) return npSurnameForm(value);
      if (desc && desc.uppercase) return npUppercaseInitials(value);
      return value;
    }

    branchColorRadios(branch) {
      const desc = this.branchDesc(branch);
      const fieldset = this.branchFieldset(branch);
      return desc && desc.colorSelector && fieldset ? fieldset.querySelectorAll(desc.colorSelector) : [];
    }

    // The active branch's on-garment ghost context, or null (no placement for it, or it failed to
    // initialise): the branch then shows the plain text card.
    activeGhost() {
      return this.ghosts[this.activeBranch()] || null;
    }

    enableBranch(branch) {
      this.fieldsets.forEach((fs) => {
        fs.disabled = fs.dataset.npBranch !== branch;
      });
      this.syncGhostVisibility();
      // The ghost figure is zero-sized while hidden (other branch active, drawer closed), so any
      // overlay computed then is empty; re-render now that (or in case) it just became visible. Not left
      // to the ResizeObserver alone: no-RO browsers would otherwise show a blank overlay until the
      // first keystroke.
      const ghost = this.activeGhost();
      if (ghost && !ghost.figure.hidden) this.renderGhostPreview(ghost);
      // Keeps the pinned preview card in sync with whichever branch is now active, on every call
      // site that switches branches (the toggle's own change listener, open(), openDrawer(),
      // restoreSnapshot(), and applyAvailability()'s re-arm) without each of those needing its own
      // separate call.
      this.updatePreviewCard();
    }

    applyFont(cls) {
      if (!cls) return;
      // ONLY the pinned preview card, not the input's own wrapper (corrected 2026-08-31, explicit
      // follow-up ask): the actual embroidery text entry box stays plain sans-serif always and
      // never switches face on a font tap, matching alt-website-horizon's brief decision 5 exactly
      // ("the field itself is plain sans-serif always... the pinned preview card is now the styled
      // element"). native-personalizer.css also forces this at the input's own site
      // (`.np-drawer input[type='text']`, !important) as a second, independent guarantee, since
      // the wrapper still carries a static `serif` class in the Liquid markup for unrelated
      // layout reasons and this JS no longer touches it either way.
      if (!this.previewCard) return;
      // Remove every font class the config declares (not just serif/script) so an E2-added font
      // with a new class name can't leave a stale class stacked on the preview card.
      this.querySelectorAll('[data-np-font]').forEach((radio) => {
        if (radio.dataset.npFont) this.previewCard.classList.remove(radio.dataset.npFont);
      });
      this.previewCard.classList.add(cls);
    }

    updateCounter(input) {
      const fieldset = input.closest('fieldset');
      const counter = fieldset ? fieldset.querySelector('[data-np-counter]') : null;
      if (counter) counter.textContent = input.value.length + '/' + input.maxLength;
    }

    // Give the charset rejection a message that names the real cause, WITHOUT re-implementing the
    // allowlist here. The `pattern` attribute in native-personalizer.liquid stays the single source
    // of truth: we ask the browser for its own verdict and only supply better wording than
    // "Please match the requested format". Clearing first is required, not cosmetic: customError
    // sticks until explicitly cleared, so without it a field that has since become valid keeps
    // reporting invalid and blocks the add.
    refreshCharsetValidity(input) {
      if (typeof input.setCustomValidity !== 'function') return;
      input.setCustomValidity('');
      if (input.validity && input.validity.patternMismatch) {
        input.setCustomValidity(input.title || 'Those characters cannot be embroidered.');
      } else if (input.hasAttribute('data-np-surname') && NP_MRS_PREFIX.test(input.value)) {
        // MRS. Surname soft validation: "MRS." is stitched for her, so a last name that already
        // starts with it would embroider "MRS. MRS. Smith". Same error-display path as the charset
        // message above (customError, cleared first on every call).
        input.setCustomValidity('Last name only. MRS. is added for you.');
      }
    }

    // The ONE routine both normalisation call sites run. It is a routine rather than a bare value
    // rewrite because every consumer of the value has to be brought along in the same breath:
    //   - the counter reads .value directly
    //   - the ghost preview re-renders off an `input` event
    //   - customError from the PREVIOUS value survives a programmatic rewrite, and a stale one is
    //     a false block: the shopper straightens nothing, the capture site canonicalises for them,
    //     and the theme then reports "emoji" against a field displaying perfectly valid text.
    // Returns true if it changed anything, so callers can skip the event churn when it is a no-op.
    normalizeField(input) {
      if (!input || typeof input.value !== 'string') return false;
      let next = npNormalizeEmbroideryText(input.value);
      // MRS. Surname is the same canonicalisation as every field, plus Title Case (Carlin 2026-10-12) and
      // the surrounding whitespace dropped (npSurnameFinal). Standard and Initials stay "exactly as
      // typed". This runs on blur (so the field shows the stitched form once the shopper leaves it, never
      // per keystroke, which would move the caret), and again before confirm, ATC capture, form submit,
      // getConfirmedProperties and the in-place edit read, so the POSTED value is always Title Case.
      if (input.hasAttribute('data-np-surname')) next = npSurnameFinal(input.value);
      // Initials are ALWAYS capitals (Carlin 2026-10-12): the posted `Initials` is uppercase, written back to
      // the field on blur (never per keystroke), and re-applied on every read like the surname above.
      if (input.hasAttribute('data-np-initials')) next = npUppercaseInitials(next);
      const changed = next !== input.value;
      if (changed) {
        input.value = next;
        this.updateCounter(input);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      // Unconditional: `close()` clears values without going through the map, so this is also the
      // hook that stops a customError set before closing from surviving into the next open.
      this.refreshCharsetValidity(input);
      return changed;
    }

    // MRS. Surname's submitted `Embroidery color` is a hidden input mirroring its display swatches,
    // written on a radio `change`. A browser form-state restore (back/forward, reload without
    // bfcache) re-checks the display radio WITHOUT firing `change` and without restoring a hidden
    // input, so the order could post the first colour while the shopper sees and confirms another.
    // Re-derive the hidden value from the checked radio at every point the value is read: ATC
    // capture / form submit / getConfirmedProperties (all via normalizeAllFields), the confirm
    // snapshot, and once at init. No-op on products without the surname branch.
    syncSurnameColorValue() {
      if (!this.hasSurname) return;
      const fieldset = this.branchFieldset('surname');
      if (!fieldset) return;
      const hidden = fieldset.querySelector('[data-np-surname-color-value]');
      const value = npCheckedRadioValue(this.branchColorRadios('surname'));
      if (hidden && value !== null && hidden.value !== value) hidden.value = value;
    }

    normalizeAllFields() {
      this.syncSurnameColorValue();
      this.querySelectorAll('input[maxlength]').forEach((input) => this.normalizeField(input));
    }

    // v2 ghost preview (BUILD.md Phase 6, S9). Display-only progressive enhancement: everything below
    // is gated on a placement figure existing (the snippet renders one only when
    // `custom.preview_placement` carries a valid placement for that branch) AND the shared math asset
    // loading AND the placement numbers validating. Any failure leaves that figure hidden and v1
    // behaviour byte-identical (fail OPEN, the opposite of the config guard). It never touches
    // cart/rush logic and never adds a line property: the overlay is SVG text with no form name.
    //
    // PER-BRANCH CONTEXTS (MRS. Surname build, 2026-10-10). The Standard figure keys on the top-level
    // placement; the surname lockup has its OWN figure, photo, placement and full-view modal
    // (`data-np-ghost-branch="surname"`), so a product can carry a surname-only placement and keep
    // the plain text card for Standard and Initials. Each context is one entry of `this.ghosts`,
    // keyed by branch, holding that branch's figure, overlay elements, input, radios, modal and
    // image-ready flag. A product with only the top-level placement builds exactly one context
    // ('standard') and behaves as it always did.
    initGhostPreview() {
      const math = window.ALTPreviewMath;
      if (!math) return;

      const contexts = {};
      this.ghostFigures.forEach((figure) => {
        const ctx = this.buildGhostContext(figure, math);
        if (ctx) contexts[ctx.branch] = ctx;
      });
      this.ghosts = contexts;
      const branches = Object.keys(contexts);
      if (!branches.length) return;

      // Coalesce renders behind rAF: each render forces synchronous layout (rect + path/text
      // measurement), and the triggers below (keystrokes, ResizeObserver ticks) fire in bursts.
      let frame = null;
      const render = () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          branches.forEach((branch) => {
            const ctx = contexts[branch];
            this.renderGhostPreview(ctx);
            // The full-view modal rides the same triggers (resize, fonts loadingdone) while open.
            // Inputs are inert behind a modal <dialog>, so text/font/colour can't change mid-open;
            // the open-click render is the one that paints the current selections.
            if (ctx.modal && ctx.modal.open) this.renderGhostModal(ctx);
          });
        });
      };

      branches.forEach((branch) => {
        const ctx = contexts[branch];
        // Reveal only once the ghost image has actually loaded, and stand back down if it errors
        // (deleted Shopify File, bad ref): a preview frame with a dead image is worse than v1.
        // ctx.ready records "image loaded"; syncGhostVisibility() turns that plus the active
        // branch into what shows. figure.hidden stays the single "preview live" flag
        // (open()/enableBranch()/openDrawer() read it).
        const reveal = () => {
          ctx.ready = true;
          this.syncGhostVisibility();
          render();
        };
        const standDown = () => {
          ctx.ready = false;
          this.syncGhostVisibility();
        };
        ctx.img.addEventListener('error', standDown);

        ctx.input.addEventListener('input', render);
        ctx.fontRadios.forEach((radio) => radio.addEventListener('change', render));
        ctx.hexRadios.forEach((radio) => radio.addEventListener('change', render));

        this.wireGhostModal(ctx);

        if (ctx.img.complete) {
          if (ctx.img.naturalWidth > 0) reveal(); // else: already failed, stay hidden
        } else {
          ctx.img.addEventListener('load', reveal);
        }
      });

      // The confirmed card's "See it on your «style»" link (2026-09-25) opens the ACTIVE branch's
      // modal. One listener for the one button; each context supplies its own openModal.
      this.confirmedViewBtn = this.querySelector('[data-np-confirmed-view]');
      if (this.confirmedViewBtn) {
        this.confirmedViewBtn.addEventListener('click', () => {
          const ghost = this.activeGhost();
          if (ghost && ghost.openModal) ghost.openModal();
        });
      }

      // The overlay is sized in pixels against the figure's rendered rect, and the exact-fit pass
      // measures real glyph runs, so re-render on: layout changes (ResizeObserver + window resize
      // as the no-RO fallback) and webfont arrival (the script face measures its cursive fallback
      // until Roundhand-bold loads; fonts 'loadingdone' re-fits with the real metrics). These are
      // page-level hooks, so connectedCallback/disconnectedCallback attach/detach them (below).
      this._ghostRender = render;
      if (window.ResizeObserver) {
        this._ghostObserver = new ResizeObserver(render);
        branches.forEach((branch) => this._ghostObserver.observe(contexts[branch].figure));
      }
      window.addEventListener('resize', render);
      if (document.fonts && document.fonts.addEventListener) {
        document.fonts.addEventListener('loadingdone', render);
      }
    }

    // One branch's ghost context, or null when its figure/placement/elements are not usable (the
    // branch then keeps the text card). `branch` is the figure's `data-np-ghost-branch`, defaulting to
    // 'standard' because the Standard figure's markup predates the attribute and is unchanged.
    buildGhostContext(figure, math) {
      const branch = figure.dataset.npGhostBranch || 'standard';
      const desc = this.branchDesc(branch);
      if (!desc) return null;
      const placement = math.parsePlacement(figure.dataset.npPlacement);
      if (!placement) return null;

      const fieldset = this.branchFieldset(branch);
      const svg = figure.querySelector('[data-np-ghost-svg]');
      const ctx = {
        branch: branch,
        lockup: !!desc.lockup,
        // The initials ghost draws the initials text card's artwork (heart symbol + typed initials)
        // instead of a text line on a path.
        mark: !!desc.mark,
        figure: figure,
        placement: placement,
        ready: false,
        modal: null,
        modalDead: false,
        openModal: null,
        canvas: figure.querySelector('[data-np-ghost-canvas]'),
        box: figure.querySelector('[data-np-ghost-box]'),
        svg: svg,
        path: svg ? svg.querySelector('path') : null,
        text: figure.querySelector('[data-np-ghost-text]'),
        textPath: figure.querySelector('[data-np-ghost-textpath]'),
        mrsPath: figure.querySelector('[data-np-ghost-mrs-path]'),
        mrsText: figure.querySelector('[data-np-ghost-mrs-text]'),
        mrsTextPath: figure.querySelector('[data-np-ghost-mrs-textpath]'),
        markEl: figure.querySelector('[data-np-ghost-mark]'),
        input: this.branchInput(branch),
        // Scoped to the branch's own fieldset: the Standard and Surname branches each carry a
        // colour row, and a bare this.querySelectorAll('[data-np-hex]') would mix the two.
        fontRadios: fieldset ? fieldset.querySelectorAll('[data-np-font]') : [],
        hexRadios: fieldset ? fieldset.querySelectorAll('[data-np-hex]') : [],
        img: figure.querySelector('img'),
        expand: figure.querySelector('[data-np-ghost-expand]')
      };
      if (!ctx.canvas || !ctx.box || !ctx.text || !ctx.input || !ctx.img) return null;
      // A text-line ghost needs its path + textPath; the initials ghost needs its mark <use> instead.
      if (ctx.mark ? !ctx.markEl : !ctx.path || !ctx.textPath) return null;
      // The surname lockup needs its second line's elements too; without them draw nothing rather
      // than a surname with no MRS.
      if (ctx.lockup && (!ctx.mrsPath || !ctx.mrsText || !ctx.mrsTextPath)) return null;
      return ctx;
    }

    // v2b full-view lightbox (Carlin 2026-07-08): click the crop window, get the FULL 2:3 ghost
    // with the same overlay in a native <dialog>. Progressive enhancement ON the progressive
    // enhancement: any missing piece (markup, showModal support) leaves the expand button hidden
    // and the crop preview exactly as it was. Wired from initGhostPreview (inside the _wired guard
    // via connectedCallback) so a reconnect can't stack duplicate listeners.
    wireGhostModal(ctx) {
      const branch = ctx.branch;
      const modal = Array.prototype.find.call(
        this.querySelectorAll('[data-np-ghost-modal]'),
        (m) => (m.dataset.npGhostBranch || 'standard') === branch
      ) || null;
      const stage = modal ? modal.querySelector('[data-np-ghost-modal-stage]') : null;
      const modalSvg = modal ? modal.querySelector('[data-np-modal-svg]') : null;
      ctx.modalEls = {
        stage: stage,
        box: modal ? modal.querySelector('[data-np-modal-box]') : null,
        svg: modalSvg,
        path: modalSvg ? modalSvg.querySelector('path') : null,
        text: modal ? modal.querySelector('[data-np-modal-text]') : null,
        textPath: modal ? modal.querySelector('[data-np-modal-textpath]') : null,
        mrsPath: modal ? modal.querySelector('[data-np-modal-mrs-path]') : null,
        mrsText: modal ? modal.querySelector('[data-np-modal-mrs-text]') : null,
        mrsTextPath: modal ? modal.querySelector('[data-np-modal-mrs-textpath]') : null,
        markEl: modal ? modal.querySelector('[data-np-modal-mark]') : null
      };
      const els = ctx.modalEls;
      const modalImg = stage ? stage.querySelector('img') : null;
      const modalClose = modal ? modal.querySelector('[data-np-ghost-modal-close]') : null;
      const modalReady =
        ctx.expand &&
        modal &&
        stage &&
        els.box &&
        els.text &&
        (ctx.mark ? els.markEl : els.path && els.textPath) &&
        (!ctx.lockup || (els.mrsPath && els.mrsText && els.mrsTextPath)) &&
        modalImg &&
        modalClose &&
        typeof modal.showModal === 'function';
      if (!modalReady) {
        ctx.modal = null; // one flag for render(): never paint a half-wired modal
        return;
      }
      ctx.modal = modal;
      ctx.expand.hidden = false;
      ctx.openModal = () => {
        // showModal, not show: top layer + inert page + native Esc/focus handling for free.
        modal.showModal();
        // Direct render, not the rAF-coalesced `render`: the stage has a rect NOW and nothing
        // else is bursting, so paint before the frame is presented (no one-frame blank text).
        // It paints from the live fields, which hold the confirmed values once the drawer has
        // closed (Cancel restores them), so the confirmed-card link shows exactly what's confirmed.
        this.renderGhostModal(ctx);
      };
      ctx.expand.addEventListener('click', ctx.openModal);
      modalClose.addEventListener('click', () => modal.close());
      // Same stand-down discipline as the figure img: a dead full-size photo (CDN eviction,
      // network drop between page load and open) must not leave text floating on a blank
      // stage. Close if open, retire the expand button; the crop preview is unaffected.
      modalImg.addEventListener('error', () => {
        if (modal.open) modal.close();
        ctx.expand.hidden = true;
        ctx.modalDead = true;
        this.syncGhostVisibility();
      });
      // Already-failed-before-wiring case (same pattern as the figure's img.complete check):
      // the error event has come and gone, so the listener above would never fire.
      if (modalImg.complete && modalImg.naturalWidth === 0) {
        ctx.expand.hidden = true;
        ctx.modalDead = true;
      }
      // Backdrop click closes: the stage fills the zero-padding dialog, so a click whose target
      // is the <dialog> itself can only have landed on the ::backdrop.
      modal.addEventListener('click', (event) => {
        if (event.target === modal) modal.close();
      });
    }

    // The pinned drawer zone shows ONE preview (2026-09-25 relocation): the active branch's
    // on-garment figure when its image has loaded, else the plain text card. The figures sit outside
    // every fieldset, so this is the only gate keeping each off the other branches. np-has-ghost (the
    // plain-input restyle of the STANDARD text field) rides the Standard figure's condition only.
    syncGhostVisibility() {
      const branches = Object.keys(this.ghosts);
      if (!branches.length) return;
      const active = this.activeBranch();
      let show = false;
      branches.forEach((branch) => {
        const ctx = this.ghosts[branch];
        const on = !!ctx.ready && active === branch;
        ctx.figure.hidden = !on;
        if (on) show = true;
      });
      if (this.previewCard) this.previewCard.hidden = show;
      // np-has-ghost (the plain-input restyle) follows the ACTIVE branch's ghost: each branch's own
      // input wrapper, if it has one, is flagged only while THAT branch's ghost is the live preview.
      // Today only the Standard text input sits in a wrapper ([data-np-preview-wrap]); Surname and
      // Initials inputs are already plain, so for them this is a no-op. For a Standard-ghost product
      // the result is identical to before (class on exactly while the Standard ghost shows).
      branches.forEach((branch) => {
        const wrapFieldset = this.branchFieldset(branch);
        const wrap = wrapFieldset ? wrapFieldset.querySelector('[data-np-preview-wrap]') : null;
        if (wrap) wrap.classList.toggle('np-has-ghost', !!(show && active === branch));
      });
      // Confirmed-card link: same condition, plus a live modal (ctx.modal is nulled when it
      // failed to wire). Only ever visible inside the confirmed card, which is itself hidden until
      // a confirm, and the drawer's branch is the confirmed branch whenever the drawer is closed.
      const activeGhost = this.ghosts[active] || null;
      if (this.confirmedViewBtn) this.confirmedViewBtn.hidden = !(show && activeGhost && activeGhost.modal && !activeGhost.modalDead);
    }

    renderGhostPreview(ctx) {
      const p = ctx.placement;
      const rect = ctx.figure.getBoundingClientRect();
      if (!rect.width || !rect.height) return; // panel hidden; open()/ResizeObserver re-render later

      // v3b crop zoom (Carlin 2026-07-08): the figure is a crop WINDOW; the canvas inside it is the
      // virtual full 2:3 ghost, sized so the placement box spans ~CROP_FOCUS of the window's width
      // and panned so the box centers in the window, clamped to the photo edges. The math asset is
      // untouched: it still just receives a container size (the canvas's), so placements calibrated
      // on the full photo land identically. Bonus: the box renders far wider in px than the old
      // full-photo figure, so BOX_MIN_H_PX no longer binds at typical placements (less drift vs the
      // calibrator, not more). ZOOM_CAP bounds a tiny box's blow-up (blur + absurd pan).
      const CROP_FOCUS = 0.35; // 0.55 -> 0.45 -> 0.35, Carlin 2026-07-08: enough garment in frame to read WHERE the stitching sits
      const GHOST_RATIO = 1.5; // ghost photos are 2:3
      const ZOOM_CAP = 4;
      // Initials frames the garment like the product's OTHER ghosts: its zoom is derived from the
      // Standard (else surname) placement's box width instead of its own small box (math.initialsCropBoxW,
      // pinned in the truth table); the pan below still centres on the initials box. Standard and
      // surname framing is untouched (zoomBoxW === p.box.w for them).
      let zoomBoxW = p.box.w;
      if (ctx.mark) {
        const refs = ['standard', 'surname']
          .map((branch) => this.ghosts[branch])
          .filter(Boolean)
          .map((other) => other.placement.box.w);
        zoomBoxW = window.ALTPreviewMath.initialsCropBoxW(p.box.w, refs);
      }
      let innerW = (CROP_FOCUS * rect.width) / zoomBoxW;
      innerW = Math.max(rect.width, Math.min(innerW, ZOOM_CAP * rect.width));
      const innerH = innerW * GHOST_RATIO;
      // The outer Math.max(0, ...) keeps the pan bound sane even if a future CSS window grows
      // taller than the 3:2 canvas (blank strip beats a negative pan).
      const panX = Math.min(Math.max(p.box.x * innerW - rect.width / 2, 0), Math.max(0, innerW - rect.width));
      const panY = Math.min(Math.max(p.box.y * innerH - rect.height / 2, 0), Math.max(0, innerH - rect.height));
      ctx.canvas.style.width = innerW + 'px';
      ctx.canvas.style.height = innerH + 'px';
      ctx.canvas.style.left = -panX + 'px';
      ctx.canvas.style.top = -panY + 'px';

      this.paintOverlay(
        ctx,
        {
          box: ctx.box,
          svg: ctx.svg,
          path: ctx.path,
          text: ctx.text,
          textPath: ctx.textPath,
          mrsPath: ctx.mrsPath,
          mrsText: ctx.mrsText,
          mrsTextPath: ctx.mrsTextPath,
          markEl: ctx.markEl
        },
        innerW,
        innerH
      );
    }

    // v2b full-view modal render: the stage IS the full 2:3 ghost (no crop/zoom/pan), so the
    // overlay math takes the stage rect directly, exactly like the calibrator takes its photo rect.
    renderGhostModal(ctx) {
      const els = ctx.modalEls;
      const rect = els.stage.getBoundingClientRect();
      if (!rect.width || !rect.height) return; // dialog closed mid-frame; the open-click renders next time
      this.paintOverlay(ctx, els, rect.width, rect.height);
    }

    // The initials overlay. Pure layout comes from math.initialsBoxToPixels (also used by the
    // calibrator); this only writes it to the elements and fits the initials to the block width.
    paintInitialsOverlay(ctx, els, w, h) {
      const math = window.ALTPreviewMath;
      const p = ctx.placement;
      const b = math.initialsBoxToPixels(p.box, w, h);
      const layout = b.layout;
      els.box.style.left = b.left + 'px';
      els.box.style.top = b.top + 'px';
      els.box.style.width = b.w + 'px';
      els.box.style.height = b.h + 'px';
      els.box.style.transform = 'rotate(' + p.rot + 'deg)';
      els.svg.setAttribute('viewBox', '0 0 ' + b.w + ' ' + b.h);
      els.markEl.setAttribute('x', 0);
      els.markEl.setAttribute('y', 0);
      els.markEl.setAttribute('width', layout.markW);
      els.markEl.setAttribute('height', layout.markH);
      const text = this.branchDisplayValue(ctx.branch, ctx.input.value);
      els.text.textContent = text;
      els.text.setAttribute('x', b.w / 2);
      els.text.setAttribute('y', layout.textY);
      els.text.setAttribute('text-anchor', 'middle');
      // The card's own ink, not a thread colour: Initials has none.
      els.text.setAttribute('style', 'font-size:' + layout.textFs + 'px;fill:var(--COLOR-TEXT, #1a1a1a)');
      // Fit pass with the real glyph run, so a wide pair never overruns the block (SVG clips silently).
      try {
        const fitted = math.exactFitFontSize(layout.textFs, b.w * math.INITIALS_TEXT_MAX_WIDTH_RATIO + math.EDGE_PAD_PX, els.text.getComputedTextLength());
        if (fitted !== layout.textFs) els.text.style.fontSize = fitted + 'px';
      } catch (error) {
        // Measurement can throw before layout; the estimate stands until the next render.
      }
    }

    // Paint one overlay (box geometry + curved text) against a virtual full-ghost container of
    // w x h pixels. Shared by the crop-window figure and the full-view modal so the two renders
    // can never drift: same math, same radios, same exact-fit pass, different target elements.
    // `ctx` supplies the branch's placement, input and radios; `els` the target elements.
    paintOverlay(ctx, els, w, h) {
      const math = window.ALTPreviewMath;
      const p = ctx.placement;

      // Initials: draw the initials text card's artwork (heart symbol over the typed initials in the
      // typewriter face, theme ink, no thread colour), the block scaled so the mark is the box's width.
      if (ctx.mark) {
        this.paintInitialsOverlay(ctx, els, w, h);
        return;
      }

      const b = math.boxToPixels(p.box, w, h);
      els.box.style.left = b.left + 'px';
      els.box.style.top = b.top + 'px';
      els.box.style.width = b.w + 'px';
      els.box.style.height = b.h + 'px';
      els.box.style.transform = 'rotate(' + p.rot + 'deg)';
      els.svg.setAttribute('viewBox', '0 0 ' + b.w + ' ' + b.h);
      els.path.setAttribute('d', math.pathD(b.w, b.h, p.arc));

      // Surname previews the Title Case form the order will carry, live, without rewriting the field.
      const text = this.branchDisplayValue(ctx.branch, ctx.input.value);
      els.textPath.textContent = text;

      // The surname lockup's name is ALWAYS script (the studio art file has no font choice); every
      // other branch reads its checked font radio.
      const fontRadio = Array.prototype.find.call(ctx.fontRadios, (r) => r.checked);
      const face = ctx.lockup ? 'script' : (fontRadio && fontRadio.dataset.npFont) || 'serif';
      const colorRadio = Array.prototype.find.call(ctx.hexRadios, (r) => r.checked);
      const hex = (colorRadio && colorRadio.dataset.npHex) || '';

      // The face class drives font-family via CSS (same families as the theme's input primitives);
      // an E2 font adds one line in native-personalizer.css alongside its theme.custom.css rule.
      els.text.setAttribute('class', 'np-face-' + face);
      const fs = math.fontSizePx(b.w, b.h, text.length, face);
      els.text.setAttribute('style', math.textStyle(fs, hex));
      // Exact-fit pass with real glyph metrics (SVG clips overruns silently).
      let fitted = fs;
      try {
        fitted = math.exactFitFontSize(fs, els.path.getTotalLength(), els.textPath.getComputedTextLength());
        if (fitted !== fs) els.text.style.fontSize = fitted + 'px';
      } catch (error) {
        // Measurement can throw before layout; the estimate stands until the next render.
        fitted = fs;
      }

      // MRS. Surname lockup: the fixed "MRS." line in serif caps, sized and lifted from the FINAL
      // surname size (so the pair shrinks together), on the surname's own curve, in the same thread
      // colour and pale-thread treatment. Proportions are the tunables in native-preview-math.js.
      if (ctx.lockup && els.mrsPath && els.mrsText) {
        const lockup = math.lockupMetrics(fitted);
        els.mrsPath.setAttribute('d', math.mrsPathD(b.w, b.h, p.arc, lockup.mrsLiftPx));
        els.mrsText.setAttribute('class', 'np-face-serif');
        els.mrsText.setAttribute('style', math.mrsTextStyle(lockup.mrsFontSize, hex));
      }
    }

    open() {
      if (!this.panel) return;
      this.panel.hidden = false;
      // ALT-DRAWER: this trigger button is being HIDDEN here (replaced by the confirmed card), not
      // "expanded". aria-expanded is set/cleared by openDrawer()/slideDrawerClosed() instead, where
      // it actually describes the drawer dialog's real open/closed state. A fresh confirm also
      // clears any still-visible "Personalization removed" toast (code-review-caught): without
      // this, removing then re-confirming a NEW personalization within the toast's 4s window left
      // the "removed" message and the freshly-confirmed card showing at the same time.
      if (this.removedNote) {
        this.removedNote.hidden = true;
        clearTimeout(this._removedNoteTimer);
      }
      if (this.openBtn) this.openBtn.hidden = true;
      this.enableBranch(this.activeBranch());
      // The figure has zero size while the panel is hidden, so the first visible render happens here
      // (belt to the ResizeObserver's braces). enableBranch above also renders, but only when the
      // active branch has a live ghost.
      const openGhost = this.activeGhost();
      if (openGhost && !openGhost.figure.hidden) this.renderGhostPreview(openGhost);

      if (this.bundleScope) {
        // Bundle mode (B2, item 5): rush visibility is a bundle-wide decision (any confirmed,
        // available component), not "this one personalizer just opened". See syncBundleRush().
        this.syncBundleRush();
      } else {
        const rush = this.rush;
        if (rush) {
          rush.show();
          // Via the getter, not rush.checkbox: a null there meant an already-ticked box (the cart-global
          // server-side pre-check) failed to re-apply `Rush: Yes` to the form, leaving a ticked box on a
          // garment that does not request rush.
          const rushCheckbox = this.rushCheckbox;
          if (rushCheckbox && rushCheckbox.checked && !this.rushDormant) rush.updateProductProperty(true);
        }
      }
      this.announceState();
    }

    // Tells the page the personalization state changed (confirmed via open(), cleared via close()),
    // in every mode, standalone included. Listened to by snippets/alt-assurance-rows.liquid, which
    // hides "Easy Returns" while anything is personalized (personalized items are final sale,
    // 2026-09-26). Announce only: no listener here, and nothing in this file depends on it.
    announceState() {
      this.dispatchEvent(new CustomEvent('np:state-change', { bubbles: true }));
    }

    close(options) {
      // Closing the panel abandons any still-pending prefill, and by default any armed edit
      // replacement too: an add unrelated to the edit must never decrement the edited line.
      // onProductAdd consumes the snapshot BEFORE calling close(), so the normal edit flow is
      // unaffected, and the fail-open teardown in initEditPrefill relies on the default drop.
      //
      // The ONE exception is removePersonalization() (the confirmed card's × and the in-drawer
      // "Remove personalization" link both funnel there), which passes keepEditSnapshot. A shopper
      // who arrived on the edit link and removed a personalization is de-personalizing THAT line;
      // the plain add that follows is the edit, not a new item, and dropping the snapshot here is
      // what left both lines in the cart (the $353 -> $557 defect, QA-LOG 2026-07-30).
      //
      // This keeps the invariant onProductAdd depends on: the snapshot is armed only by
      // initEditPrefill, immediately after it calls openDrawer(), and every other close() drops it,
      // so "panel hidden AND snapshot armed" can only mean removePersonalization() was called. It
      // stays one-shot regardless: onProductAdd consumes it on the first add on this form.
      if (!(options && options.keepEditSnapshot)) this._npEditSnapshot = null;
      this._npPrefillInvalid = true;
      // ALT-DRAWER (Codex-caught, 2026-08-31): close() is "return to pristine idle", so it has to
      // drop lastConfirmed unconditionally, not just the fields. Without this, close() after a
      // SUCCESSFUL add (onProductAdd's own call, below) left the old snapshot armed; reopening the
      // drawer and cancelling with nothing newly typed took revertDrawerEdits()'s prior-confirmed
      // branch, which RE-ENABLES the branch fieldset and refills it with that stale design, while
      // panel.hidden stayed true (looking idle) and the trigger stayed visible. The next PLAIN add
      // to cart would then silently carry the old personalization properties, because an enabled
      // fieldset serializes into FormData regardless of what the visible UI is showing.
      this.lastConfirmed = null;
      if (this.panel) this.panel.hidden = true;
      if (this.openBtn) {
        this.openBtn.setAttribute('aria-expanded', 'false');
        this.openBtn.hidden = false;
      }
      this.fieldsets.forEach((fs) => {
        fs.disabled = true;
      });
      this.querySelectorAll('input[maxlength]').forEach((input) => {
        input.value = '';
        this.updateCounter(input);
        // Emptying .value does not clear a customError set against the old text, and customError
        // outlives the value it was about. Without this, typing an emoji, closing the panel and
        // reopening it leaves the field permanently invalid and the shopper unable to add anything.
        this.refreshCharsetValidity(input);
      });
      // ALT-DRAWER (Codex-caught): the preview card only updates on the input's own `input` event,
      // which the blanking above never dispatches, without this, cancelling or removing a
      // personalization left the OLD text floating above a now-empty field. Also clears the
      // thread-colour custom property (removeProperty, not set to '') since a cleared field has
      // no colour to show either.
      this.updatePreviewCard();
      if (this.previewCard) this.previewCard.style.removeProperty('--np-thread-color');

      if (this.bundleScope) {
        // Bundle mode (B2, item 5): see syncBundleRush(). Whether the shared rush control hides
        // here depends on every OTHER personalizer in the bundle too, not just this one closing.
        this.syncBundleRush();
      } else {
        const rush = this.rush;
        if (rush) {
          rush.hide();
          // Uncheck the box as well as removing the property; otherwise a later open() sees checkbox.checked
          // still true and silently re-applies Rush: Yes (a paid line) the shopper never re-selected.
          // Via the getter, not rush.checkbox: a null there left the box visibly ticked while the
          // property below was removed, i.e. exactly the stale state this line exists to prevent.
          // Writing `.checked` on the resolved input is not writing to custom.js's internals.
          const rushCheckbox = this.rushCheckbox;
          if (rushCheckbox) rushCheckbox.checked = false;
          rush.updateProductProperty(false);
        }
      }
      this.announceState();
    }

    // ALT-DRAWER (ported from the closed alt-website-horizon project's proven drawer design,
    // 2026-08-31). The state machine deliberately layers on top of open()/close() above rather than
    // replacing them: open() already does exactly what "reveal a confirmed personalization" needs
    // (unhide panel, enable the branch, render the ghost preview, and reconcile the rush checkbox's
    // real property against its checked state (see :899-907 above, already proven and unchanged), and
    // close() already does exactly what "clear everything" needs (disable every fieldset, blank the
    // fields, hide+uncheck+un-prop rush, unhide the trigger). Reusing them for the drawer's confirm/
    // remove outcomes means the two money-critical transitions run through the SAME code every other
    // path in this file already exercises, rather than a parallel implementation built for this port.
    //
    // Three states, not two:
    //   idle       - drawer closed, panel.hidden=true, trigger visible.
    //   editing    - drawer open (openDrawer()), active fieldset enabled so typing/preview work,
    //                but panel.hidden stays TRUE (nothing is confirmed yet). The onAtcCapture click-
    //                gate above blocks an add attempt during this state; see its own comment for why
    //                that has to be an active check, not just showModal()'s top-layer inertness.
    //   confirmed  - drawer closed, panel.hidden=FALSE (via open(), called at the end of
    //                confirmDrawer()), confirmed card visible, rush revealed. This is the state
    //                onAtcCapture/onProductAdd/the atomic rush add all read as "personalized".
    //
    // Cancel/Esc/backdrop distinguish two cases (revertDrawerEdits): if a personalization was
    // already confirmed, they revert the visible fields to that last-confirmed snapshot and just
    // close the dialog (the confirmed state itself is untouched); if nothing was ever confirmed,
    // they fall through to the same full clear as Remove.

    // Focus the active branch's text input (standard text, initials or surname). Needs the drawer open and
    // the branch enabled first (openDrawer() does both synchronously); a disabled or hidden input
    // silently ignores focus(), so the checks just keep this a no-op when the drawer didn't open.
    focusActiveInput() {
      if (!this.drawer || !this.drawer.open) return;
      const input = this.branchInput(this.activeBranch());
      if (!input || input.matches(':disabled')) return;
      input.focus({ preventScroll: true });
    }

    openDrawer() {
      if (!this.drawer || typeof this.drawer.showModal !== 'function') return;
      // Codex-caught (2026-08-31): the confirmed card (and its pencil) render SYNCHRONOUSLY at the
      // start of confirmDrawer(), before the exit animation even starts, so a fast pencil-tap can
      // reach here while the dialog is still technically `.open` from that same confirm's closing
      // animation. showModal() on an already-open <dialog> throws. Finish any pending close
      // synchronously first (also clears the 400ms backstop timer, so a re-opened drawer can never
      // be yanked shut later by a stale timer from the PREVIOUS close), slideDrawerClosed() itself
      // resets the closing attribute/flag, so nothing further needed here for that.
      if (this.drawer.open) this.slideDrawerClosed();
      // Clean slate: clear the freeze confirmDrawer() applies below, on every fieldset, regardless
      // of which branch was frozen. A re-edit must always start fully interactive.
      this.fieldsets.forEach((fs) => {
        fs.inert = false;
      });
      if (this.openBtn) this.openBtn.setAttribute('aria-expanded', 'true');
      if (this.editBtn) this.editBtn.setAttribute('aria-expanded', 'true');
      this.drawer.showModal();
      // AFTER showModal(), not before: the figure has zero size while the dialog is closed, so a
      // render attempted earlier is a silent no-op (same ordering open() below already uses, for
      // the same reason, belt to the ResizeObserver's braces for browsers without one).
      this.enableBranch(this.activeBranch());
      const drawerGhost = this.activeGhost();
      if (drawerGhost && !drawerGhost.figure.hidden) this.renderGhostPreview(drawerGhost);
    }

    // First call: start the exit animation and return (mirrors Broadcast's own quick-add modal
    // idiom, theme.dev.js:7109-7192, modalClose() there does the identical two-call dance). Second
    // call (from the dialog's own `animationend` listener wired in connectedCallback, or the
    // setTimeout backstop below): perform the real close. The backstop exists because the two-phase
    // close only completes if `animationend` actually fires, today guaranteed by the generic
    // `dialog[closing]` rule already in theme.css, but a future more-specific rule could shadow it,
    // and a stuck `drawerClosing` flag would silently wedge every subsequent open/close.
    closeDrawer() {
      if (!this.drawer || !this.drawer.open) return;
      if (this.drawerClosing) return;
      this.drawerClosing = true;
      this.drawer.setAttribute('closing', '');
      clearTimeout(this._drawerCloseBackstop);
      this._drawerCloseBackstop = setTimeout(() => this.slideDrawerClosed(), 400);
    }

    slideDrawerClosed() {
      clearTimeout(this._drawerCloseBackstop);
      this.drawer.removeAttribute('closing');
      this.drawerClosing = false;
      // Both possible openers: only one is ever visible at a time (the trigger pre-confirm, the
      // pencil post-confirm), but setting aria-expanded on whichever is currently hidden is
      // harmless and keeps each one correct for whenever it becomes the visible control again.
      if (this.openBtn) this.openBtn.setAttribute('aria-expanded', 'false');
      if (this.editBtn) this.editBtn.setAttribute('aria-expanded', 'false');
      const wasConfirming = this._npConfirming;
      this._npConfirming = false;
      if (this.drawer.open) this.drawer.close();
      // Horizon F5 fix, ported (Codex round 3: must run AFTER .close() above, not before, while
      // the dialog is still modal, everything outside it, including the pencil, is inert and
      // .focus() silently does nothing). The dialog's native focus-restore otherwise falls back to
      // <body> here, because the element that had focus before showModal() (the trigger button) is
      // now `hidden` and therefore unfocusable.
      if (wasConfirming && this.editBtn) this.editBtn.focus();
    }

    // Cancel, the header ×, Esc, and backdrop-click all funnel here.
    revertDrawerEdits() {
      if (this.lastConfirmed) {
        this.restoreSnapshot(this.lastConfirmed);
      } else {
        this.close();
      }
      this.closeDrawer();
    }

    // The confirmed card's × and the in-drawer "Remove personalization" link both funnel here.
    // keepEditSnapshot matches today's exact remove-button semantics (D-R10.6): removing a
    // personalization during an edit-link session IS the edit, so the armed replacement has to
    // survive and be consumed by the plain add that follows, retiring the original cart line.
    removePersonalization() {
      const hadConfirmed = !!this.lastConfirmed; // captured BEFORE close() nulls it
      this.close({ keepEditSnapshot: true }); // close() itself drops lastConfirmed unconditionally
      this.closeDrawer();
      if (hadConfirmed) this.showRemovedNote();
      // BUNDLE MODE (B2, finding #4). close() above has already dropped panel.hidden to true, so
      // any bundle-host listener reading getConfirmedProperties() when this fires sees the final,
      // settled state, before closeDrawer()'s own exit animation even finishes. Standalone never
      // sees this: gated on bundleScope, the same gate every other bundle-mode branch in this file
      // uses, so standalone behaviour is unchanged.
      if (this.bundleScope) this.dispatchEvent(new CustomEvent('np:bundle-removed', { bubbles: true }));
    }

    showRemovedNote() {
      if (!this.removedNote) return;
      this.removedNote.hidden = false;
      clearTimeout(this._removedNoteTimer);
      this._removedNoteTimer = setTimeout(() => {
        this.removedNote.hidden = true;
      }, 4000);
    }

    confirmDrawer() {
      if (this.drawerClosing) return;
      const branch = this.activeBranch();
      const fieldset = this.querySelector('fieldset[data-np-branch="' + branch + '"]');
      if (!fieldset) return;
      const confirmDesc = this.branchDesc(branch);
      const input = confirmDesc ? fieldset.querySelector(confirmDesc.input) : null;
      // Canonicalise BEFORE validity/snapshot (Horizon F1(b) fix): a shopper who confirms via
      // Enter or a direct tap, never triggering the blur listener, must not see an un-normalized
      // character on the confirmed card even though the field-level canonicalisation elsewhere
      // would already have fixed it before the cart ever saw it.
      if (input) this.normalizeField(input);
      // Validate the INPUT, never the fieldset (code-review-caught, 2026-08-31): a <fieldset> is
      // spec-barred from constraint validation, so `willValidate` is always false on it and
      // `reportValidity()` on a fieldset always returns true regardless of any invalid descendant
      // control. Checking the fieldset here would silently let an unembroiderable character (one
      // that survives normalizeField's substitution table) through Confirm, showing a "confirmed"
      // state that the theme's own later `form.checkValidity()` would still correctly reject at
      // add-to-cart time, by which point the field lives inside a closed, invisible <dialog> with
      // nothing left to anchor a validation bubble to.
      if (!input || !input.reportValidity()) return; // a failing check aborts Confirm; drawer stays open
      this.lastConfirmed = this.snapshotBranch(branch, fieldset);
      // open() unhides panel (panel.hidden=false, the money-path signal), re-affirms the branch
      // stays enabled, renders the ghost preview, hides the trigger, and reconciles the rush
      // checkbox's real property, all reused unchanged from the pre-drawer inline flow.
      this.open();
      this.renderConfirmedCard(this.lastConfirmed);
      // Freeze the values at the root, not just defend against their drift downstream (code-review
      // "altitude" finding, 2026-08-31): the exit animation leaves the dialog genuinely open and its
      // fieldset genuinely enabled for ~250-400ms, so a keystroke landing in that window could
      // otherwise diverge from what was just snapshotted. `inert` (not `disabled`, which would drop
      // the fieldset from FormData) makes the whole fieldset un-interactive, no typing, no radio
      // clicks, while its values, still live in the DOM, keep serializing normally. This turns
      // "might drift, so reassert on every possible downstream read" into "cannot drift", which is
      // why onAtcCapture/onFormSubmit/slideDrawerClosed no longer need their own reassert calls.
      // Cleared unconditionally at the top of the next openDrawer(), regardless of which branch.
      fieldset.inert = true;
      this._npConfirming = true; // tells slideDrawerClosed() to move focus to the pencil once closed
      this.closeDrawer();
      // BUNDLE MODE (B2, finding #4). Fires only once every guard above has passed (a failing
      // reportValidity() already returned early) and lastConfirmed/panel.hidden are committed, so
      // a bundle-host listener reading getConfirmedProperties() here sees the final state, before
      // closeDrawer()'s own exit animation even finishes — this is what lets alt-bundle.js drop
      // its old 300ms post-click wait. Standalone never sees this: gated on bundleScope, same as
      // removePersonalization()'s own dispatch above and every other bundle-mode branch in this
      // file, so standalone behaviour is unchanged.
      if (this.bundleScope) this.dispatchEvent(new CustomEvent('np:bundle-confirmed', { bubbles: true }));
    }

    // Captures the active branch's live values off the controls themselves, never off the DOM the
    // confirmed card renders, so a later re-edit-then-cancel always restores real field state.
    snapshotBranch(branch, fieldset) {
      this.syncSurnameColorValue();
      const desc = this.branchDesc(branch);
      const snap = { branch: branch };
      const input = desc ? fieldset.querySelector(desc.input) : null;
      snap.text = input ? input.value : '';
      if (desc && desc.fontRadios) {
        const fontRadio = Array.prototype.find.call(fieldset.querySelectorAll('[data-np-font]'), (r) => r.checked);
        snap.font = fontRadio ? fontRadio.value : '';
        // The CSS class (serif/script), not just the display label: renderConfirmedCard() needs
        // this to actually render the confirmed text in the chosen face, matching
        // .np-confirmed.script/.serif .np-confirmed-text (native-personalizer.css).
        snap.fontClass = fontRadio ? fontRadio.dataset.npFont || '' : '';
      } else if (desc && desc.fixedFont) {
        // MRS. Surname: no font radios; the font is the fixed value the hidden input writes, and the
        // lockup card sets its own faces, so there is no CSS class to carry.
        snap.font = desc.fixedFont;
        snap.fontClass = '';
      }
      if (desc && desc.colorSelector) {
        const colorRadio = Array.prototype.find.call(fieldset.querySelectorAll(desc.colorSelector), (r) => r.checked);
        snap.color = colorRadio ? colorRadio.value : '';
        snap.colorHex = colorRadio ? colorRadio.dataset.npHex || '' : '';
      }
      return snap;
    }

    // Restores a prior snapshot's values onto the live controls (used by Cancel when a
    // personalization was already confirmed, the confirmed state itself is not touched, only the
    // fields the shopper had started re-editing).
    restoreSnapshot(snap) {
      if (!snap) return;
      const toggle = Array.prototype.find.call(this.branchToggles, (t) => t.value === snap.branch);
      if (toggle && !toggle.checked) {
        toggle.checked = true;
        toggle.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        this.enableBranch(snap.branch);
      }
      const fieldset = this.branchFieldset(snap.branch);
      const desc = this.branchDesc(snap.branch);
      if (!fieldset || !desc) return;
      const input = fieldset.querySelector(desc.input);
      if (input) {
        input.value = snap.text || '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      if (desc.fontRadios) {
        const fontRadio = Array.prototype.find.call(fieldset.querySelectorAll('[data-np-font]'), (r) => r.value === snap.font);
        if (fontRadio && !fontRadio.checked) {
          fontRadio.checked = true;
          fontRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      if (desc.colorSelector) {
        const colorRadio = Array.prototype.find.call(fieldset.querySelectorAll(desc.colorSelector), (r) => r.value === snap.color);
        if (colorRadio && !colorRadio.checked) {
          colorRadio.checked = true;
          colorRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    // Renders the snapshot's values into the confirmed card. Display-only: nothing here carries a
    // name= attribute, so none of it can reach FormData, the real fields (still enabled, per
    // open() above) remain the actual source of truth for the eventual add.
    renderConfirmedCard(snap) {
      if (!snap || !this.panel) return;
      const desc = this.branchDesc(snap.branch);
      const isInitials = !!(desc && desc.mark);
      const isLockup = !!(desc && desc.lockup);
      const textEl = this.panel.querySelector('[data-np-confirmed-text-value]');
      if (textEl) textEl.textContent = this.branchDisplayValue(snap.branch, snap.text || '');
      this.setPreviewMark(this.panel.querySelector('[data-np-confirmed-mark]'), isInitials);
      this.panel.classList.toggle('np-typewriter', isInitials);
      // MRS. Surname lockup: the fixed "MRS." line over the surname, both tinted to the thread colour
      // via --np-thread-color on the panel. Cleared on every other branch so a re-edit that switches
      // type can't leave the lockup styling or a stale colour behind.
      this.panel.classList.toggle('np-lockup', isLockup);
      const mrsEl = this.panel.querySelector('[data-np-confirmed-mrs]');
      if (mrsEl) mrsEl.hidden = !isLockup;
      if (isLockup && snap.colorHex) this.panel.style.setProperty('--np-thread-color', snap.colorHex);
      else this.panel.style.removeProperty('--np-thread-color');
      // Code-review-caught (2026-08-31): the confirmed card never actually rendered in the chosen
      // face, it always fell back to whatever .np-confirmed-text inherits, because nothing ever
      // added the .serif/.script class .np-confirmed.script/.serif .np-confirmed-text
      // (native-personalizer.css) needs. Remove every font class the config declares first (not
      // just serif/script, matching applyFont()'s own reasoning) so a re-edit that switches font
      // can't leave a stale class stacked from the PREVIOUS confirm.
      this.querySelectorAll('[data-np-font]').forEach((radio) => {
        if (radio.dataset.npFont) this.panel.classList.remove(radio.dataset.npFont);
      });
      if (desc && desc.fontRadios && snap.fontClass) this.panel.classList.add(snap.fontClass);
      const fontEl = this.panel.querySelector('[data-np-confirmed-font]');
      const sepEl = this.panel.querySelector('[data-np-confirmed-sep]');
      const dotEl = this.panel.querySelector('[data-np-confirmed-dot]');
      const colorEl = this.panel.querySelector('[data-np-confirmed-color]');
      // F6(b) (Horizon fix, ported): a branch without a font or colour hides those spans rather than
      // leave a dangling "· " separator and an empty ringed swatch dot. Initials hides all four;
      // Standard shows all four; MRS. Surname has a colour but a FIXED font (nothing for the shopper
      // to read), so it hides the font and the separator and keeps the swatch dot + colour name.
      const showFont = !!(desc && desc.fontRadios);
      const showColor = !!(desc && desc.colorSelector);
      if (fontEl) fontEl.hidden = !showFont;
      if (sepEl) sepEl.hidden = !(showFont && showColor);
      if (dotEl) dotEl.hidden = !showColor;
      if (colorEl) colorEl.hidden = !showColor;
      if (showFont && fontEl) fontEl.textContent = snap.font || '';
      if (showColor) {
        if (dotEl) dotEl.style.backgroundColor = snap.colorHex || '';
        if (colorEl) colorEl.textContent = snap.color || '';
      }
    }

    // Shared by the confirmed card and the initials-branch live preview. toggleAttribute, never the
    // `.hidden` IDL property: `hidden` only exists on HTMLElement and silently no-ops on an
    // SVGElement (a real bug Horizon hit and fixed, writing `el.hidden = false` on the <svg> left
    // the actual `hidden` attribute untouched, keeping the mark invisible on a live device walk).
    setPreviewMark(el, show) {
      if (!el) return;
      el.toggleAttribute('hidden', !show);
    }

    // The pinned drawer preview card (visual-correction pass, 2026-08-31, matching
    // alt-website-horizon's design): a plain textContent mirror, not canvas/SVG text rendering,
    // shared by BOTH branches. Mirrors whichever branch is currently ACTIVE (not necessarily
    // enabled-and-typed-in — activeBranch() reads the type-toggle radios, which is correct here
    // since this only ever runs while the drawer is open and the fieldsets are wired to match).
    // Font class and thread colour are kept in sync separately, by applyFont() and the colour-name
    // mirror above, since those only change on their own radio's `change` event, not on every
    // keystroke.
    updatePreviewCard() {
      if (!this.previewCardText) return;
      const branch = this.activeBranch();
      const desc = this.branchDesc(branch);
      const isInitials = !!(desc && desc.mark);
      const input = this.branchInput(branch);
      this.previewCardText.textContent = input ? this.branchDisplayValue(branch, input.value) : '';
      this.setPreviewMark(this.previewCardMark, isInitials);
      if (this.previewCard) this.previewCard.classList.toggle('np-typewriter', isInitials);
      // Everything below exists only on a style that offers the MRS. Surname type, so Standard and
      // Initials styles run exactly the code above and nothing else.
      if (!this.hasSurname || !this.previewCard) return;
      // The lockup: "MRS." stacked over the surname (surname branch only).
      const isLockup = !!(desc && desc.lockup);
      this.previewCard.classList.toggle('np-lockup', isLockup);
      const mrsEl = this.previewCard.querySelector('[data-np-preview-card-mrs]');
      if (mrsEl) mrsEl.hidden = !isLockup;
      // Two branches now own a colour row but there is ONE shared card, and applyThreadColor only
      // runs on a radio's own `change`. Re-derive the card's thread colour from the ACTIVE branch's
      // checked swatch on every branch switch, or picking red under Surname would leave a red card
      // behind when the shopper flips back to Standard's black.
      const checked = Array.prototype.find.call(this.branchColorRadios(branch), (r) => r.checked);
      if (checked && checked.dataset.npHex) this.previewCard.style.setProperty('--np-thread-color', checked.dataset.npHex);
      else if (desc && desc.colorSelector) this.previewCard.style.removeProperty('--np-thread-color');
    }

    onProductAdd(event) {
      const button = event && event.detail && event.detail.button;
      if (!button || button.closest('form') !== this.form) return; // not this product's form
      // Consume the atomic-add garment id HERE, unconditionally, so it is one-shot on EVERY branch
      // below and not just the one that reaches addedGarmentLine. Doing it inside addedGarmentLine
      // alone was not enough: a rushed add with no edit snapshot never calls it (the call sits
      // inside the `if (this._npEditSnapshot)` block), so the id survived to the next add.
      const atomicGarmentVariantId = this._npAtomicGarmentVariantId;
      this._npAtomicGarmentVariantId = null;
      // Any add on this form invalidates a still-pending edit prefill (the shopper moved on
      // before the prefill's /cart.js read resolved; arming a replacement now would decrement a
      // line against an add that had nothing to do with the edit).
      this._npPrefillInvalid = true;
      if (!this.panel) return;
      // A hidden panel normally means nothing was personalized, so there is nothing to sync. The one
      // exception is the de-personalizing edit: removePersonalization() hides the panel but keeps
      // the snapshot armed (close({ keepEditSnapshot: true })), and the plain add that follows
      // IS the edit and must retire the original line. Every other close() nulls the snapshot, so
      // hidden + armed is unambiguous and cannot let an unrelated add reach the replace below.
      if (this.panel.hidden && !this._npEditSnapshot) return;

      // No rush handling here any more: a rushed add is atomic (onAtcCapture), so by the time this
      // fires the paid rush line is already in the same cart response as the garment.

      // R5 replace-on-add: this add came from an edit-link visit, so retire one unit of the original
      // line. One-shot by design: the snapshot is consumed HERE, before any async work, so a failed
      // replacement can never fire again against a later, unrelated add-to-cart. The response line
      // rides along only to CLASSIFY the edit (changed vs unchanged) by identity, never by key:
      // keys can churn between the add response and the /cart.js re-read (discount-state churn).
      if (this._npEditSnapshot) {
        const snapshot = this._npEditSnapshot;
        this._npEditSnapshot = null;
        // A hidden panel with the snapshot still armed can only be the remove-button path (see
        // close()), and close() disabled every property input, so this add carries no
        // personalization: it is a de-personalizing edit. Tell the resolver, because it cannot
        // infer it -- Shopify reports the propertyless line as `properties: null`, which the
        // classifier correctly refuses to read as authoritative.
        const dePersonalized = this.panel.hidden;
        this.replaceEditedLine(
          snapshot,
          npClassifiableAddedLine(this.addedGarmentLine(event.detail.response, atomicGarmentVariantId)),
          dePersonalized
        );
      }

      this.close();
    }

    // Which line in an add response is the GARMENT, for edit classification only.
    //
    // A rushed add is now a TWO-item response (garment + bare rush) and npClassifiableAddedLine
    // refuses any multi-item response by design, because in general items[0] is not guaranteed to be
    // the garment. We posted this request, so we can name the garment, but we name it by VARIANT ID,
    // never by position. Position would be an unverified assumption about Shopify's response
    // ordering, and the cost of it being wrong is the one failure this file refuses to allow: if the
    // rush line came back first with `properties: {}`, an UNCHANGED edit whose original line had
    // meanwhile gone (legacy sweep, second tab) would classify as a CHANGED edit, bypass the
    // quantity-1 guard in npResolveReplaceTarget, and decrement the customer's just-paid line to 0.
    // No match, or an ambiguous one, returns null = unclassifiable, which routes the resolver to its
    // conservative branch (leaves a visible duplicate at worst, never a lost line).
    addedGarmentLine(response, variantId) {
      // `variantId` is PASSED IN, already consumed by onProductAdd (changed 2026-07-31,
      // /code-review triage). It used to read and clear this._npAtomicGarmentVariantId itself, on
      // the multi-item path only, which contradicted atomicRushAdd's own "consumed and cleared"
      // comment and left a stale id behind two ways: every single-item response returned before
      // reaching the clear (reachable whenever cartHasRushLine returns true, since the rush line
      // already being in the cart makes ours a ONE-item add), and a rushed add with no edit
      // snapshot never calls this method at all. A later multi-item post from ProductBundle would
      // then match against a variant id from an unrelated earlier add. Consuming in the single
      // caller makes it one-shot on every branch.
      if (!response || typeof response !== 'object') return null;
      // A single-variant add (the theme's own path, and ours when the rush line was already in cart)
      // returns the line itself, or a one-item array; both are unambiguous.
      if (!Object.prototype.hasOwnProperty.call(response, 'items')) return response;
      if (!Array.isArray(response.items)) return null;
      if (response.items.length === 1) return response.items[0];
      // `variantId` above is the id we actually POSTed. Deliberately not re-queried from the DOM
      // here: the `id` input lives OUTSIDE the <form> element and is associated with it via form="…"
      // (sections/product.liquid:302 vs the form at product-buttons.liquid:127), so a
      // form.querySelector('[name="id"]') would return null and silently make every rushed edit
      // unclassifiable. FormData reads it correctly because it walks form.elements, which includes
      // form-associated controls; a subtree query does not.
      // A multi-item response with nothing stashed is not a shape we produced. Stay conservative.
      if (!variantId) return null;
      // Match on `variant_id` ONLY, the same field npIdentityMatches compares. Accepting an `id`
      // fallback here would let the two disagree in the dangerous direction: a line selected by `id`
      // but carrying no `variant_id` fails npIdentityMatches, reads as a CHANGED edit, and so bypasses
      // the quantity-1 guard and decrements a just-paid line to 0. No match → null → conservative.
      const matches = response.items.filter((item) => item && String(item.variant_id) === String(variantId));
      return matches.length === 1 ? matches[0] : null;
    }

    // Capture-phase gate for the atomic rush add. Returns immediately, leaving the theme's normal add
    // path completely untouched, unless this click is really the add-to-cart button AND a paid rush
    // line is really needed. Every plain add, every non-rushed personalized add, and every add on a
    // page where the rush product is unreadable therefore behaves exactly as it did before R10.
    onAtcCapture(event) {
      const target = event.target;
      const button = target && target.closest ? target.closest('[data-add-to-cart]') : null;
      if (!button || button !== this.atcButton()) return;

      // ALT-DRAWER click-gate (required, not defense-in-depth): while the drawer is open and its
      // active fieldset ENABLED for typing, an add-to-cart click that reaches the theme would post
      // whatever is currently typed even though it was never Confirmed. showModal()'s top-layer
      // inertness blocks a REAL click on this button from outside the dialog, but it does not block
      // a SYNTHETIC one: the Enter-key interceptor below now routes to confirmDrawer() while the
      // drawer is open, but the sticky cart bar (assets/product.js) dispatches its own synthetic
      // bubbling click on this exact button independently of drawer state, and that path must be
      // blocked too. Hard stop, not a bare early return: an unconfirmed draft must never reach the
      // theme's own add path either.
      //
      // NOT gated on bare `this.drawer.open`: that stays true throughout the ~250ms exit animation
      // AFTER a successful Confirm too (closeDrawer() only sets [closing] and returns; the dialog's
      // own .open flips false only once slideDrawerClosed() actually calls .close()), which would
      // wrongly block a legitimate add attempt landing in that window. `!this.drawerClosing`
      // excludes exactly that window while still catching a pencil-reopened re-edit of an already-
      // confirmed personalization (openDrawer() explicitly resets drawerClosing to false), where
      // panel.hidden is unhelpful on its own: it stays false the whole time (still reflecting the
      // PRIOR confirm) even though the fields now hold an unconfirmed draft of a NEW edit.
      if (this.drawer && this.drawer.open && !this.drawerClosing) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // Canonicalise BEFORE any other early return, and before the theme sees the form at all.
      // This placement is load-bearing in two directions and must not be moved down:
      //
      //   1. The three returns immediately below (button disabled, panel hidden, rush unchecked)
      //      cover the MAJORITY add path: every plain add and every non-rushed personalized add
      //      leaves through one of them. Normalising underneath them would silently skip almost
      //      every order and only ever canonicalise the rushed minority.
      //   2. On iOS a <button> does not take focus on tap, so the blur listener has frequently NOT
      //      fired by the time this runs. This is the only site guaranteed to see the final value
      //      on exactly the devices whose smart punctuation created the problem.
      //
      // Capture phase on the FORM is what buys the ordering: the theme's checkValidity() and
      // FormData read both happen inside cartAddEvent, invoked synchronously from a TARGET-phase
      // listener on this button, and a capture listener on an ancestor always precedes it. The
      // button click listener wired in connectedCallback is too late for the same reason: theme.js
      // is head-deferred and registered its handler before this file even defined the element.
      // Synthetic clicks (sticky cart bar, the Enter interceptor) propagate through capture too.
      this.normalizeAllFields();

      if (button.hasAttribute('disabled')) return;

      // In-place edit gate (2026-10-10). Placed BEFORE the panel-hidden return below because it does
      // its own panel check (a hidden panel is the de-personalizing edit, which stays on the add
      // path) and before the rush gate because a non-dormant rushed add must keep its atomic path
      // (tryInPlaceEdit declines it). `_npBypassInPlace` is set by inPlaceEdit's fallback, which
      // re-clicks this button so the theme's own add-first path runs exactly as before; it is read
      // and cleared on every pass so it can only ever skip ONE gate, never linger.
      const bypassInPlace = this._npBypassInPlace;
      this._npBypassInPlace = false;
      if (!bypassInPlace && this._npEditSnapshot && this.tryInPlaceEdit(event, button)) return;

      if (!this.panel || this.panel.hidden) return; // nothing personalized → theme path
      // Resolve the checkbox by QUERYING the element, never through custom.js's `rush.checkbox`.
      // That property is assigned once in the constructor via this.querySelector (custom.js:75), so
      // an element upgraded before its children were parsed carries null for life. This is the same
      // unreliable reference resolveRushControl was built to stop trusting (:318-323), and this gate
      // was still reading it.
      //
      // Why that was a money bug rather than a style nit (found by /code-review 2026-07-30): the
      // change listener custom.js attaches is on the ELEMENT, not the checkbox
      // (`this.addEventListener('change', …)`, custom.js:85), so a shopper ticking the box still
      // reaches updateProductProperty and still puts `Rush: Yes` on the garment form even when
      // `this.checkbox` is null. This gate would then bail to the theme path, which posts the
      // garment carrying `Rush: Yes` with NO $65 line. custom.js's legacy listener cannot cover:
      // its own guard dereferences the same null (`this.checkbox.checked`, custom.js:78) and throws.
      // 1.5s later removeRushProductFromCart strips `Rush` off the garment, and settleRush is gone,
      // so nothing repairs it. Net: the shopper asked for rush, was not charged for it, and the
      // order does not record it. Reachable on a PLAIN rushed add, not just via the edit prefill.
      //
      // A synchronous read is correct here even though resolveRushControl is a bounded async
      // waiter: this is a capture-phase click handler, and for the shopper to have ticked the box
      // at all the element must already be present and upgraded. There is nothing to wait for.
      // Dormant PDP Rush: never an atomic Rush add, and never a garment stamped `Rush: Yes` (a stray
      // hidden input from any other writer is removed before the theme reads the form).
      if (this.rushDormant) {
        const form = this.form;
        if (form) form.querySelectorAll('input[name="properties[Rush]"]').forEach((input) => input.remove());
        return;
      }
      const rushCheckbox = this.rushCheckbox;
      if (!rushCheckbox || !rushCheckbox.checked) return; // no rush line needed → theme path
      const form = this.form;
      if (!form) return;
      // Let the theme own both preflight bails it already implements (cartAddEvent): an invalid form
      // must report and stay put, and a file input is a hard return there.
      if (!form.checkValidity()) return;
      if (form.querySelector('[type="file"]')) return;

      const rushProduct = window.theme && window.theme.rushProduct;
      const rushVariant = rushProduct && rushProduct.variants && rushProduct.variants[0];
      const garment = this.buildGarmentItem(form);
      // The ticked checkbox is NOT sufficient authority to charge $65. The old two-call path keyed off
      // the `Rush: Yes` property having actually reached the cart (custom.js:164 only adds the line
      // when the add response carries it), and that invariant is kept here: `updateProductProperty`
      // returns early when it cannot find the form, so a ticked box can coexist with a garment that
      // has no Rush property. Adding the paid line in that state charges for a rush the garment does
      // not request, and the legacy sweep then deletes the line it just charged for.
      const rushRequested = !!garment && garment.properties && garment.properties['Rush'] === 'Yes';
      if (!rushProduct || !rushVariant || !rushVariant.id || !garment || !rushRequested) {
        // Fail open to the theme's add: a shopper must never be blocked from buying. Logged loudly
        // because the theme's add then carries `Rush: Yes` with no $65 line, and custom.js's legacy
        // listener cannot be relied on to cover for us (it fires only when its parentId matches, and
        // it reads a stale snapshot). Pre-existing exposure, not introduced here: the old two-call
        // path needed the same rushProduct global and the same property.
        console.error('[native-personalizer] rush is checked but the atomic add could not be built; the rush line will be missing', {
          hasRushProduct: !!rushProduct,
          hasRushVariant: !!(rushVariant && rushVariant.id),
          hasGarment: !!garment,
          rushPropertyPresent: rushRequested
        });
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      // The theme's listener we just suppressed also records the focus-restore target, so the cart
      // drawer knows where to put focus when it closes. Do it here or a rushed add drops focus.
      if (window.a11y) window.a11y.lastElement = button;
      this.atomicRushAdd(garment, rushProduct, rushVariant.id, button);
    }

    // Synchronous eligibility gate for the in-place edit (see inPlaceEdit). Returns true only when it
    // has TAKEN the click (preventDefault + stopPropagation, so the theme's own add never runs);
    // false leaves the click completely untouched for the existing paths. Everything cheap and
    // knowable from the DOM is decided here; what needs the live cart (is the line still there, is
    // it quantity 1) is decided inside inPlaceEdit, which falls back to the add path on any "no".
    tryInPlaceEdit(event, button) {
      const snapshot = this._npEditSnapshot;
      if (!snapshot || this.bundleScope) return false;
      const form = this.form;
      if (!form) return false;
      // A hidden panel is the de-personalizing edit (removePersonalization keeps the snapshot armed
      // with the panel hidden): that add carries NO properties, which /cart/change would silently
      // ignore (P2), so it stays on the add-then-decrement path.
      if (!this.panel || this.panel.hidden) return false;
      if (snapshot.quantity !== 1) return false;
      // Same preflight the theme's cartAddEvent runs: an invalid form must report and stay put (the
      // theme's own path does that), and a file input is a hard return there.
      if (!form.checkValidity()) return false;
      if (form.querySelector('[type="file"]')) return false;
      // Dormant PDP Rush: strip any stray `properties[Rush]` input BEFORE reading the form, exactly as
      // onAtcCapture's dormant branch does (which this gate runs ahead of). The frozen custom.js
      // refreshRushProductInPDP runs on every theme:cart:change even when dormant and, with the Rush
      // product in the cart, appends a hidden `properties[Rush]=Yes` input; reading it here would
      // write `Rush: Yes` onto the edited line through /cart/change.
      if (this.rushDormant) form.querySelectorAll('input[name="properties[Rush]"]').forEach((input) => input.remove());
      const garment = this.buildGarmentItem(form);
      if (!garment || String(garment.id) !== String(snapshot.variantId)) return false;
      if ((garment.selling_plan ? String(garment.selling_plan) : null) !== snapshot.sellingPlanId) return false;
      if (garment.quantity !== 1) return false;
      if (!garment.properties || Object.keys(garment.properties).length === 0) return false;
      // A non-dormant rushed add is owned by the atomic rush path (garment + paid Rush line in ONE
      // /cart/add); an in-place change would leave the Rush line unpaid. Dormant Rush (the bag owns
      // it since 2026-09-30) never stamps `Rush: Yes`, so it does not reach here.
      if (!this.rushDormant && garment.properties['Rush'] === 'Yes') return false;

      event.preventDefault();
      event.stopPropagation();
      // The theme's listener we just suppressed also records the focus-restore target (same
      // restoration atomicRushAdd's gate does by hand).
      this._npPrefillInvalid = true;
      if (window.a11y) window.a11y.lastElement = button;
      button.classList.add('is-loading');
      button.disabled = true;
      this.inPlaceEdit(snapshot, garment, button);
      return true;
    }

    // The garment half of the request.
    buildGarmentItem(form) {
      // Built from FormData, never from a hand-rolled input scrape, for two reasons: FormData excludes
      // DISABLED fieldsets, which is the invariant the whole branching design rests on (see file
      // header), and it walks form.elements, so it picks up the controls that live OUTSIDE the form
      // and are associated with it via form="…" (the `name="id"` input at sections/product.liquid:302,
      // the preorder property below it). A subtree querySelector would miss both.
      const data = new FormData(form);
      const variantId = data.get('id');
      if (!variantId) return null;
      const garment = { id: String(variantId), quantity: Number(data.get('quantity')) || 1, properties: {} };
      const sellingPlan = data.get('selling_plan');
      if (sellingPlan) garment.selling_plan = String(sellingPlan);
      data.forEach((value, key) => {
        const match = /^properties\[(.+)\]$/.exec(key);
        if (match) garment.properties[match[1]] = value;
      });
      return garment;
    }

    // Does the cart already hold a rush line that will SURVIVE, i.e. one we can rely on to cover this
    // add? Authoritative /cart.js read, NOT the rendered snapshot the rush component uses
    // (custom.js:152): the snapshot is only as fresh as the last cart render, so a cross-tab change
    // makes it wrong in both directions, and with settleRush gone nothing repairs either mistake.
    //
    // "Survives" is the important half, and it is why this is not a plain product-id test. An ORPHAN
    // rush line (one with no `Rush: Yes` garment beside it) is already condemned: the legacy sweep
    // deletes it (custom.js:267) on the next pass. Counting it as coverage loses the $65 silently:
    // shopper removes a rushed garment (sweep scheduled), adds another rushed garment inside that
    // 1.5s window, we skip the rush item because a rush line "exists", then the sweep deletes it and
    // the new garment carries `Rush: Yes` with nothing to pay for it. Treating an orphan as absent
    // instead means we post our own line, so the worst case is a briefly visible duplicate rather
    // than a silent unpaid upgrade. Duplicate-over-silent-and-unpaid is the deliberate choice.
    //
    // CORRECTED 2026-07-31 (/code-review, QA-LOG triage). This comment used to justify that choice
    // with "the same sweep normalises it back to 1 (custom.js:244-262)". THAT IS FALSE. That code
    // takes items.filter(...)[0], fixes THAT ONE LINE'S QUANTITY, and returns; it never removes a
    // second rush LINE. Deleted settleRush step 2 was the only thing that did. So a genuine
    // duplicate rush LINE is unrepaired and would stay in the cart. The decision above is unchanged
    // and still correct (a visible duplicate beats an invisible unpaid upgrade), but it rests on
    // being visible to the shopper, NOT on self-healing. Reachability is narrow today: a bare add
    // MERGES into an existing bare rush line, and custom.js's own addRushProductToCart posts no
    // properties either, so two DISTINCT lines need a propertied rush line that nothing currently
    // creates. Narrow is not the same as impossible; do not restore the self-healing claim.
    //
    // Falls back to the rendered snapshot if /cart.js is unreadable. NOTE the camelCase there:
    // cart-items-json.liquid emits `productId` / `isRush`, NOT `product_id`.
    async cartHasRushLine(rushProduct) {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (res.ok) {
          const items = (await res.json()).items || [];
          // Rush is order-level since the cart Rush section (2026-09-29, RUSH-TO-CART-PLAN.md): a Rush
          // line covers the order while ANY personalised line (Embroidery text or Initials) is in the
          // cart, whether or not a garment carries `Rush: Yes` (a cart-added Rush line stamps none).
          // Requiring the stamp here made a later rushed PDP add buy a second Rush unit.
          const hasRushLine = items.some((item) => String(item.product_id) === String(rushProduct.id));
          const hasPersonalisedLine = items.some(
            (item) => String(item.product_id) !== String(rushProduct.id) && item.properties &&
              (String(item.properties['Embroidery text'] || '').trim() !== '' || String(item.properties['Initials'] || '').trim() !== '')
          );
          return hasRushLine && hasPersonalisedLine;
        }
        console.warn('[native-personalizer] rush pre-check: /cart.js returned ' + res.status + ', falling back to the rendered cart');
      } catch (error) {
        console.warn('[native-personalizer] rush pre-check: /cart.js unreadable, falling back to the rendered cart', error);
      }
      try {
        const node = document.querySelector('cart-items script[type="application/json"]');
        if (!node) return false;
        const items = (JSON.parse(node.textContent) || {}).items || [];
        // Same order-level rule on the rendered cart JSON (snippets/cart-items-json.liquid), where
        // `isRush` now marks the Rush line itself, so personalised lines are read from `properties`.
        return (
          items.some((item) => String(item.productId) === String(rushProduct.id)) &&
          items.some(
            (item) => String(item.productId) !== String(rushProduct.id) && item.properties &&
              (String(item.properties['Embroidery text'] || '').trim() !== '' || String(item.properties['Initials'] || '').trim() !== '')
          )
        );
      } catch (error) {
        // Both reads failed → assume no rush line and add one. Deliberate failure direction: a
        // duplicate $65 line is VISIBLE in the drawer, where the shopper can remove it, whereas a
        // MISSING rush line is invisible, unpaid, and the legacy sweep then strips the garment's
        // `Rush: Yes` (custom.js:223-241), losing the upgrade silently.
        // Same correction as the block above (2026-07-31): this used to also claim the sweep
        // "normalises it to qty 1 (custom.js:244-262)". That code only fixes ONE line's quantity
        // and never removes a second LINE. Visibility is the whole safety argument here.
        return false;
      }
    }

    // One /cart/add for garment + rush, then hand straight back to the theme's own post-add chain.
    // Mirrors theme.js addToCart (loading state → add → theme:product:add → getCart) and the
    // in-production multi-item add in ProductBundle.onSubmit (custom.js:686-700). We deliberately
    // reuse the theme's events instead of reimplementing the drawer: theme:cart:refresh → getCart →
    // build → theme:product:added is what opens it.
    async atomicRushAdd(garment, rushProduct, rushVariantId, button) {
      // Stopping propagation means the button's own listener above (and the form submit listener)
      // never run, so the initiation-time prefill invalidation has to happen HERE. Without it a
      // still-pending prefill could apply mid-add and mis-attribute this add as the edit.
      this._npPrefillInvalid = true;
      // Name the garment for addedGarmentLine by the id we are about to post, never by position in
      // the response. Set fresh on every add; addedGarmentLine consumes and clears it.
      this._npAtomicGarmentVariantId = garment.id;

      button.classList.add('is-loading');
      button.disabled = true;

      // Garment FIRST: if Shopify ever turns out to process the array sequentially and stop at a
      // failure, a garment failure then adds nothing at all, which is the safe direction (a rush-line
      // failure after a successful garment is no worse than today's two-call behaviour).
      const items = [garment];
      // Exactly one $65 line per cart, ever. Same intent as the bundle path (custom.js:625-629,679-684):
      // a second rushed garment must not add a second rush line, because the legacy sweep only
      // normalises the quantity 1.5s later and a checkout inside that window collects $130. See
      // cartHasRushLine for why an ORPHAN rush line deliberately does not count as coverage.
      if (!(await this.cartHasRushLine(rushProduct))) {
        items.push({ id: String(rushVariantId), quantity: 1 });
      }

      const addUrl = (window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add.js';
      try {
        const res = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        });
        const response = await res.json();
        if (!res.ok || response.status) {
          this.atomicRushAddError(response, button);
          return;
        }
        // Clear our own loading state before dispatching rather than relying on productAddCallback to
        // do it: the theme's addToCart clears inline AND dispatches, and if no cart element is mounted
        // to service the event the button would otherwise spin, disabled, forever.
        button.classList.remove('is-loading');
        button.disabled = false;
        // theme:product:add drives the theme's productAddCallback (adds is-added, manages the cart-bar
        // button) AND our own onProductAdd (edit replace + close), exactly as the theme's add does.
        button.dispatchEvent(
          new CustomEvent('theme:product:add', { detail: { response: response, button: button }, bubbles: true })
        );
        // KNOWN LIMITATION, deliberately not fixed (2026-07-31, DECISIONS D-R10.7). This navigation
        // cancels the in-flight replaceEditedLine that the dispatch two lines above just started:
        // its /cart.js read and decrement are killed mid-flight and the edited line survives as a
        // duplicate. Latent today, `settings_data.json` is `drawer`.
        //
        // Not fixed because fixing OUR half would be misleading. dispatchEvent is synchronous but
        // does not hand listener promises back, so awaiting the replacement means a clear-before-
        // dispatch instance-field handoff across the event boundary, on a live money path. And
        // theme.js's own addToCart contains the IDENTICAL dispatch-then-navigate, which the
        // de-personalizing edit (the whole reason replaceEditedLine exists) rides, because that add
        // is a plain one that never reaches this method. We cannot touch that half without editing
        // theme.js. So the work would add risk here while leaving the likelier route broken.
        // IF CART TYPE IS EVER CHANGED TO `page`, both halves must be done together.
        if (window.theme && theme.settings && theme.settings.cartType === 'page') {
          window.location = theme.routes.cart_url;
          return;
        }
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { detail: response }));
      } catch (error) {
        // Transport failure. Deliberately NOT logged as "nothing was added": /cart/add is not
        // idempotent and the request may well have committed server-side with only the response lost,
        // so the outcome is genuinely unknown here. Refresh the cart so the drawer shows the real
        // state and the shopper's retry decision is an informed one, and re-enable the button, which
        // the theme also does on its own .catch (addToCart).
        console.error('[native-personalizer] atomic rush add did not complete; cart state unknown, refreshing', error);
        this.atomicRushAddError(null, button);
        document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
      }
    }

    // Mirror of the cart element's addToCartError (a method we cannot reach from here): put the
    // theme's own errors markup in the form's error container, then fire theme:product:add-error so
    // productAddCallback clears the button state and product.js scrolls the form into view.
    // Two deliberate deviations: the message is inserted as TEXT, not interpolated into an innerHTML
    // template, and a transport failure with no Shopify message shows no message rather than the
    // theme's "Failed to fetch: undefined".
    atomicRushAddError(data, button) {
      // Clear the button state INLINE before anything else, for the same reason the success path
      // does it at :1136-1137 rather than trusting the event: productAddCallback is registered by
      // the cart element's connectedCallback, so on any page with no cart element mounted the
      // theme:product:add-error dispatched below is serviced by nobody and the button stays
      // disabled and spinning forever. The shopper's only exit is a reload, which is the same
      // dead-end class as the mitigation rejected in D-R10.5. Both error paths route through here
      // (the !res.ok / response.status branch and the transport catch), so this covers both.
      if (button) {
        button.classList.remove('is-loading');
        button.disabled = false;
      }
      // Consume the stashed garment id on the FAILURE path too. onProductAdd is what normally
      // consumes it, but this method dispatches theme:product:add-ERROR, which onProductAdd does
      // not listen for, so a failed rushed add used to leave the id set. A later multi-item post
      // from ProductBundle on the same form would then classify against an id from the add that
      // failed. This is the branch that made the "one-shot on EVERY branch" claim untrue.
      this._npAtomicGarmentVariantId = null;
      const container = this.form ? this.form.querySelector('[data-cart-errors-container]') : null;
      const message =
        data && data.description && data.message !== data.description
          ? data.message + ': ' + data.description
          : (data && (data.message || data.description)) || '';
      if (container && message) {
        const errors = document.createElement('div');
        errors.className = 'errors';
        errors.appendChild(document.createTextNode(message));
        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'errors__close';
        close.setAttribute('data-close-error', '');
        close.innerHTML =
          '<svg aria-hidden="true" focusable="false" role="presentation" width="24px" height="24px" stroke-width="1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor" class="icon icon-cancel"><path d="M6.758 17.243L12.001 12m5.243-5.243L12 12m0 0L6.758 6.757M12.001 12l5.243 5.243" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
        close.addEventListener('click', () => {
          container.classList.remove('is-visible');
          container.innerHTML = '';
        });
        errors.appendChild(close);
        container.innerHTML = '';
        container.appendChild(errors);
        container.classList.add('is-visible');
      }
      button.dispatchEvent(
        new CustomEvent('theme:product:add-error', { detail: { response: data, button: button }, bubbles: true })
      );
    }

    // Edit the line IN PLACE: one /cart/change rewriting its properties (2026-10-10).
    //
    // Why not the add-first path for this case: it posts the new design with /cart/add and only
    // then decrements the old line. When the edited unit is the LAST one in stock, the add is
    // refused (422 "already sold out", the old line is already holding the stock) and the edit
    // silently does nothing. Editing the line where it sits has no stock question at all.
    //
    // The guarantee: a single /cart/change either rewrites the line or leaves it untouched, so no
    // ordering of steps here can lose the shopper's paid line. That is the same invariant the add-first
    // order buys (worst case: a visible duplicate, never a lost line), reached without a second unit.
    // Change-then-add was considered and rejected: it reopens the very window add-first closes
    // (the old line is changed or gone, then the add fails for a NON-stock reason and the design is
    // lost).
    //
    // Probes behind each guard (live storefront, 2026-10-10; full list at npInPlaceEditTarget):
    //   P1 the change applies to a stock-capped line; P2 `properties: {}` and the like can return
    //   200 yet change nothing, so success is judged by npChangeLanded on the RESPONSE cart, never by
    //   the status code; P3 a change that equals another line's identity merges the two (quantity
    //   summed), which is why npChangeLanded ignores quantity.
    //
    // Failure map, by what the cart can have become:
    //   - Nothing was sent (cart read or parse failed, gate says no) or the change was REJECTED with
    //     a 4xx: the line is provably untouched, so the theme's add path is safe. This includes an
    //     exception thrown before the POST was issued (`sent` below).
    //   - The POST was issued and the outcome is unknown (transport failure, a 5xx answer, an
    //     unreadable response), or it returned ok but the readback does not show the new design while
    //     the ORIGINAL line is gone: the change very likely applied. A fallback add would then create
    //     a PAID DUPLICATE of the design, so none is attempted: refresh the cart and let the shopper
    //     see the truth. A 5xx counts here because a gateway (502/504) can reply after the backend
    //     committed.
    //   - It returned ok, the readback does not show the new design AND the original line is still
    //     there: the P2-class silent no-op. The line is untouched, so the add path is safe.
    async inPlaceEdit(snapshot, garment, button) {
      const fallBack = (why) => {
        console.warn('[native-personalizer] edit in place skipped: ' + why + '; using the add path');
        button.classList.remove('is-loading');
        button.disabled = false;
        // Something else consumed or replaced the armed snapshot while we were awaiting: re-clicking
        // would run an add against a different (or no) edit. Restore the button and stop.
        if (this._npEditSnapshot !== snapshot) return;
        // The synthetic click re-enters onAtcCapture, which consumes this flag and lets the theme's
        // own add-first path run exactly as before, with the snapshot still armed for onProductAdd.
        // Cleared again straight after the (synchronous) dispatch so a click swallowed by an earlier
        // gate cannot leave it armed.
        this._npBypassInPlace = true;
        button.click();
        this._npBypassInPlace = false;
      };
      const outcomeUnknown = (why, error) => {
        // Do NOT fall back (see the failure map). Refresh the cart so the drawer shows the real state
        // and re-enable the button. The snapshot stays armed: a retry click re-runs the gate, which
        // re-reads the cart; if the change HAD committed, npFindEditedLine fails on the old
        // properties, so the retry takes the add path, whose resolver then touches nothing. Worst
        // case is a visible duplicate or the theme's 422, never a lost line.
        console.error('[native-personalizer] edit in place ' + why + '; cart state unknown, refreshing', error || '');
        this.atomicRushAddError(null, button);
        document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
      };

      // The success block, shared by the two ways an edit can be done: our own /cart/change just
      // landed, or an earlier attempt already did (see the pre-check in the try below).
      const finishSuccess = (cartDetail, logMessage) => {
        // One-shot: the snapshot is consumed so no later add on this form can retire anything.
        this._npEditSnapshot = null;
        button.classList.remove('is-loading');
        button.disabled = false;
        // The sticky cart bar's own button (product.js) took is-loading + disabled on click and is only
        // cleared by productAddCallback on theme:product:add / add-error, neither of which an in-place
        // edit fires. Clear it by hand, along with any error banner an earlier failed attempt left.
        const scope = (this.form && this.form.closest('product-component')) || document;
        scope.querySelectorAll('[data-add-to-cart-bar]').forEach((bar) => {
          bar.classList.remove('is-loading');
          bar.removeAttribute('disabled');
        });
        const errors = this.form ? this.form.querySelector('[data-cart-errors-container]') : null;
        if (errors) {
          errors.classList.remove('is-visible');
          errors.innerHTML = '';
        }
        console.info(logMessage);
        this.close();
        // cartType 'page' mirrors atomicRushAdd: navigate to the cart instead of refreshing a drawer.
        if (window.theme && theme.settings && theme.settings.cartType === 'page') {
          window.location = theme.routes.cart_url;
          return;
        }
        // theme:cart:refresh, NOT theme:product:add: an edit is not an add (and that event's
        // detail.response is line-shaped, which the theme's listeners would misread).
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { detail: cartDetail }));
      };

      // The try holds ONLY requests and their parsing. Every decision to fall back is recorded in
      // `fallbackReason` and acted on AFTER it, so an exception thrown by fallBack's synthetic click
      // (a click listener) can never be caught here and misread as a transport failure.
      let fallbackReason = null;
      let decision = null;
      let updated = null;
      let sent = false;
      let alreadyApplied = null;
      try {
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!cartRes.ok) {
          fallbackReason = 'cart read failed (' + cartRes.status + ')';
        } else {
          const cart = await cartRes.json();
          // LOST-REPLY RETRY: the edit may already be in the cart. After a transport error following a
          // committed /cart/change the snapshot stays armed, the shopper retries, and the gate below
          // would find the ORIGINAL line gone and send them down the add path (a 422 banner on a
          // capped variant, a paid duplicate otherwise). So ask first whether the new design is
          // already there (npChangeLanded: new identity present, old identity absent). The same test
          // also passes for an UNCHANGED edit whose line still exists, which is a no-op either way.
          // Nothing is sent; `decision` stays null on this path. Known blind spot, accepted: cart state
          // cannot tell a lost reply from "the original line was removed elsewhere while another line
          // already carries exactly the new design". That case also reports success with nothing sent;
          // nothing is lost, and the cart refresh that follows shows the shopper the truth.
          if (npChangeLanded(cart.items, garment, snapshot)) {
            alreadyApplied = cart;
          } else {
            const gate = npInPlaceEditTarget(cart.items, snapshot, garment);
            if (!gate.target) {
              fallbackReason = gate.reason;
            } else {
              decision = gate;
              const changeUrl = (window.theme && theme.routes && theme.routes.cart_change_url) || '/cart/change.js';
              // From here the cart may change, so any exception means outcome unknown, not "untouched".
              sent = true;
              // selling_plan is deliberately NOT sent: the gate guarantees it is unchanged, P1 shows it
              // is not needed, and sending it could alter the allocation.
              const changed = await fetch(changeUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: gate.target.key, quantity: 1, properties: garment.properties })
              });
              // 4xx: Shopify refused the request, the line is untouched, so the add path is safe. 5xx:
              // a gateway (502/504) can answer AFTER the backend committed, so the outcome is unknown,
              // exactly like a transport failure. Routed through the catch below via a throw so it
              // shares that branch (sent is already true).
              if (changed.status >= 500) throw new Error('change returned ' + changed.status + ', outcome unknown');
              if (!changed.ok) fallbackReason = 'change rejected (' + changed.status + ')';
              else updated = await changed.json();
            }
          }
        }
      } catch (error) {
        if (sent) {
          outcomeUnknown('did not complete', error);
          return;
        }
        // Thrown before the POST was issued (cart read or parse): nothing was changed.
        fallbackReason = 'cart read threw (' + (error && error.message) + ')';
      }

      if (fallbackReason) {
        fallBack(fallbackReason);
        return;
      }

      if (alreadyApplied) {
        finishSuccess(alreadyApplied, '[native-personalizer] edit in place: already applied, nothing sent');
        return;
      }

      const updatedItems = updated && updated.items;
      if (!npChangeLanded(updatedItems, garment, snapshot)) {
        if (!npFindEditedLine(updatedItems, snapshot).line) {
          // The original line is gone (or the response carries no readable cart): the change most
          // likely applied and only the readback failed to compare equal. A fallback add now would
          // duplicate the design at full price.
          outcomeUnknown('returned ok but the readback does not show the new design and the original line is gone');
        } else {
          fallBack('change returned ok but the new design is not in the cart');
        }
        return;
      }

      finishSuccess(updated, '[native-personalizer] edit in place: properties updated, resolved by ' + decision.via);
    }

    // Decrement the edited line by one, addressed by KEY or by identity FINGERPRINT (variant +
    // selling plan + full properties), never positional (the rush-bug class). DECREMENT, not
    // remove: an UNCHANGED edit re-add MERGES into one line (qty + 1), a changed edit leaves the
    // old line to retire (1 -> 0 removes it). The armed key alone is not trustworthy: discount-
    // state churn can rekey every line between prefill and here, so a missing key falls back to
    // the fingerprint, and the decrement decision on BOTH paths is npResolveReplaceTarget's truth
    // table (including the quantity-1 may-be-the-added-line guard). Sequencing is add-new-FIRST
    // (we only run from onProductAdd, after the add landed), so the worst failure here is a
    // visible duplicate line, never a lost paid line.
    //
    // Since 2026-10-10 the common same-variant edit (quantity-1 line, new design) goes IN PLACE via
    // inPlaceEdit and never reaches here. This path serves the remaining edits: a size/variant or
    // selling-plan change, a qty>1 line, de-personalizing, a rushed add, and every fallback from
    // inPlaceEdit (where the snapshot is still armed for onProductAdd).
    async replaceEditedLine(snapshot, addedLine, dePersonalized) {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) {
          console.error('[native-personalizer] edit replace: cart read failed (' + res.status + '); a duplicate line may remain');
          return;
        }
        const cart = await res.json();
        const resolved = npResolveReplaceTarget(cart.items, snapshot, addedLine, dePersonalized);
        if (!resolved.target) {
          // Nothing safely addressable: stale key with no unique fingerprint, or the lone match
          // may be the customer's just-added line. Touch nothing.
          console.warn('[native-personalizer] edit replace: ' + resolved.reason + '; leaving cart as-is');
          return;
        }
        const changeUrl = (window.theme && theme.routes && theme.routes.cart_change_url) || '/cart/change.js';
        const changed = await fetch(changeUrl, {
          method: 'post',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: resolved.target.key, quantity: Math.max(0, resolved.target.quantity - 1) })
        });
        if (!changed.ok) {
          console.error('[native-personalizer] edit replace: decrement failed (' + changed.status + '); a duplicate line may remain');
          return;
        }
        const updated = await changed.json();
        // Success is logged (not just failures) so QA and the R10 retest can OBSERVE which
        // resolution path ran; key churn is otherwise invisible when the replace works.
        console.info('[native-personalizer] edit replace: decremented one unit, resolved by ' + resolved.via + ' (' + resolved.reason + ')');
        // Only announce after a confirmed successful change.
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { detail: updated }));
      } catch (error) {
        console.error('[native-personalizer] edit replace failed; a duplicate line may remain (never a lost line)', error);
      }
    }

    // Maps a cart line's properties to ONE branch (see npResolveEditLine). A method so the bundle
    // host (assets/alt-bundle.js) can reuse the exact mapping the standalone cart-edit prefill uses.
    resolveEditLine(props) {
      return npResolveEditLine(props);
    }

    // Validates that THIS personalizer's DOM can represent a resolved line, before anything is
    // touched: the branch's fieldset and text input exist, the text is non-empty and within the
    // input's maxlength, and every radio the line names (font for Standard, thread colour for
    // Standard and Surname) exists. Radios are matched by iterating and comparing .value, scoped to
    // the branch's own fieldset: never interpolate cart values into CSS selectors (quotes and special
    // characters would break them), and never match across branches (both Standard and Surname
    // carry a colour row). Liquid escaped the value= attributes, the parser decoded them into
    // .value, and cart JSON is plain text, so a heart and friends compare correctly here.
    // Returns {fieldset, input, fontRadio, colorRadio} (radios null when the branch has none) or null.
    editControls(resolved) {
      if (!resolved) return null;
      const desc = this.branchDesc(resolved.branch);
      const fieldset = this.branchFieldset(resolved.branch);
      if (!desc || !fieldset) return null;
      const input = fieldset.querySelector(desc.input);
      const text = resolved.text;
      if (!input || typeof text !== 'string' || !text.length) return null;
      if (input.maxLength > 0 && text.length > input.maxLength) return null;
      const matchRadio = (radios, value) => Array.prototype.find.call(radios, (r) => r.value === value) || null;
      let fontRadio = null;
      let colorRadio = null;
      if (desc.fontRadios) {
        fontRadio = matchRadio(fieldset.querySelectorAll('input[type="radio"][name="properties[Embroidery font]"]'), resolved.font);
        if (!fontRadio) return null;
      }
      if (desc.colorSelector) {
        colorRadio = matchRadio(fieldset.querySelectorAll(desc.colorSelector), resolved.color);
        if (!colorRadio) return null;
      }
      return { fieldset: fieldset, input: input, fontRadio: fontRadio, colorRadio: colorRadio };
    }

    // R5 cart edit-link prefill. If the URL carries ?np_edit=<line key> and that line belongs to
    // THIS product, reopen the panel with the line's design filled in and arm replace-on-add.
    // The whole path is fail-open and ATOMIC: we validate that every part of the old line can be
    // represented by the current config BEFORE touching any UI state. On any mismatch (stale key,
    // other product, unknown font/color, over-limit text) we abort untouched: normal PDP, no
    // _npEditSnapshot, no analytics, and the param is only stripped after a successful consume.
    async initEditPrefill() {
      // Two-phase for fail-open honesty: before `applying` flips, an abort leaves a byte-untouched
      // v1 PDP; after it flips (open() onward), an exception tears back down via close(), so the
      // worst reachable end state is a plain closed panel, never a half-armed edit.
      let applying = false;
      try {
        if (!window.URLSearchParams || !window.URL) return;
        const key = new URLSearchParams(window.location.search).get('np_edit');
        if (!key) return;

        const productId = this.dataset.productId;
        if (!productId || !this.panel) return;

        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const cart = await res.json();
        const line = cart.items.find((i) => i.key === key);
        if (!line || String(line.product_id) !== String(productId)) return;

        // The line must be a well-formed native/Zepto contract line for exactly ONE branch.
        // Anything ambiguous or out-of-contract aborts: prefilling defaults over a line we cannot
        // fully represent would let a re-add silently REPLACE the shopper's design with a
        // different one (worse than no prefill at all).
        // The branch comes from `Personalization Type` when the line carries it (the only way to
        // tell Standard from MRS. Surname, which both write `Embroidery text`), else today's
        // key-based inference, so single-type lines still work. npResolveEditLine owns that mapping
        // and the per-branch key checks; alt-bundle.js's bundle prefill calls the same function via
        // resolveEditLine(), so the two can never disagree.
        const props = line.properties || {};
        const has = (k) => Object.prototype.hasOwnProperty.call(props, k);
        if (has('Rush') && props['Rush'] !== 'Yes') return;
        const resolved = this.resolveEditLine(props);
        if (!resolved) return;
        const branch = resolved.branch;
        const text = resolved.text;

        // Validate the full mapping before mutating anything (atomicity): the branch's fieldset,
        // its input (non-empty text within maxlength) and every radio the line names.
        const controls = this.editControls(resolved);
        if (!controls) return;
        const input = controls.input;
        const fontRadio = controls.fontRadio;
        const colorRadio = controls.colorRadio;

        // Branch selection: a multi-type product exposes toggles; a single-type product has none,
        // in which case the lone fieldset must BE the target branch (validated above via fieldset).
        const toggle = Array.prototype.find.call(this.branchToggles, (t) => t.value === branch) || null;
        if (!toggle && this.activeBranch() !== branch) return;

        // Resolve the rush control BEFORE the mutation phase, and refuse to prefill if it cannot be
        // resolved. It used to be a bare `if (rush && rush.checkbox)` around the restore, which
        // SILENTLY SKIPPED on a miss; measured 1 in 5 on a rushed line's edit link (QA-LOG
        // 2026-07-29). On a `Rush: Yes` line that silence is a money loss: the re-add carries no Rush
        // property, so onAtcCapture's rushRequested gate correctly declines, the theme adds the
        // garment un-rushed, the old rushed line is retired, and the legacy sweep then deletes the
        // now-orphaned $65 line. Nothing is left to notice: settleRush is gone.
        //
        // Two independent reasons the miss happens, hence a waiter rather than a re-query:
        // custom.js assigns `this.checkbox` in the CONSTRUCTOR via this.querySelector, so an element
        // upgraded before its children were parsed carries a null checkbox for life; and `this.rush`
        // is a live document query that misses an element which has not been parsed yet.
        // We therefore resolve the checkbox OURSELVES rather than trusting rush.checkbox, and only
        // need the element upgraded for show()/updateProductProperty(), neither of which reads it.
        const rushResolution = await this.resolveRushControl();
        const rushControl = rushResolution.control;
        if (!rushControl) {
          // Fail CLOSED, both directions, before anything is applied (so the cart line and the panel
          // are untouched and the shopper just gets a normal PDP):
          //  - a rushed line we cannot re-check would silently lose the rush, as above;
          //  - a non-rushed line we cannot un-check can silently GAIN it, because
          //    rush-personalization.liquid:22 renders the checkbox pre-checked whenever ANY rush
          //    product is in the cart. That cart-global default is the whole reason this restore
          //    forces both ways.
          // Only a product whose template renders no <rush-personalization> at all is exempt, and
          // then solely for a non-rushed line, where neither failure is reachable.
          // `rushResolution.elementSeen`, never a fresh `this.rush` read: the guard must agree with
          // what the waiter observed, or it can exempt a page that does have a rush element.
          if (props['Rush'] === 'Yes' || rushResolution.elementSeen) {
            console.warn(
              '[native-personalizer] edit prefill aborted: the rush control did not resolve, so the line\'s rush state cannot be restored safely',
              { lineIsRushed: props['Rush'] === 'Yes', rushElementPresent: rushResolution.elementSeen }
            );
            return;
          }
        }

        // Staleness guards, checked LAST (after every await, immediately before mutation): the
        // /cart.js read races real shopper activity. If the element left the DOM, the shopper
        // opened the drawer themselves (typing requires opening it, so this covers all manual
        // input), a personalization is already confirmed, or ANY add-to-cart already fired on this
        // form (onProductAdd sets the flag), the prefill is stale and must die without touching
        // anything. ALT-DRAWER: `this.panel.hidden` now means "confirmed", not "editor open", so
        // the drawer's own `.open` has to be checked alongside it to cover the manual-input case.
        if (!this.isConnected || this._npPrefillInvalid) return;
        if ((this.drawer && this.drawer.open) || this.panel.hidden === false) return;

        // Validation passed: apply. From here on, every dispatch drives the same listeners a real
        // shopper's clicks would (counter, ghost preview, font class, color-name mirror).
        //
        // ALT-DRAWER: opens the DRAWER in edit mode, not the confirmed card, preserves today's
        // "immediately editable" behaviour and avoids fabricating a confirmed state that never went
        // through confirmDrawer()'s own reportValidity() check (deliberate deviation from the
        // Horizon source, whose drawer was the only way personalization state was ever set at all;
        // this edit path is a different entry point and doesn't inherit that constraint).
        applying = true;
        this.openDrawer();
        // Code-review-caught (2026-08-31): openDrawer() silently no-ops (missing <dialog>, or a
        // runtime with no showModal()) rather than throwing, so its failure has to be checked
        // explicitly here, the atomicity this whole function promises ("validate everything
        // before touching any UI state") would otherwise be broken: every dispatch below, the
        // rush restore, the armed edit snapshot, and the np_edit URL strip would all proceed as if
        // the drawer had genuinely opened, on a runtime where it silently didn't. A later unrelated
        // plain add-to-cart would then find `_npEditSnapshot` armed with nothing visibly open to
        // explain why, and could decrement/retire the original cart line for a shopper who never
        // saw an edit surface at all.
        if (!this.drawer.open) return;
        if (toggle && !toggle.checked) {
          toggle.checked = true;
          toggle.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          this.enableBranch(branch);
        }
        input.value = text;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        if (fontRadio && !fontRadio.checked) {
          fontRadio.checked = true;
          fontRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (colorRadio && !colorRadio.checked) {
          colorRadio.checked = true;
          colorRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Rush state is forced BOTH ways, line-specifically. The rush checkbox is cart-global
        // (custom.js checks it whenever ANY rush product is in the cart), so without the explicit
        // uncheck, editing a non-rushed line while another garment is rushed would silently
        // re-add Rush: Yes to the edited line.
        // rushControl is null only on a template with no <rush-personalization> at all, and then only
        // for a non-rushed line (guarded above), where there is nothing to restore either way.
        // Dormant PDP Rush: a legacy `Rush: Yes` line is re-added without the stamp. Its Rush line
        // stays in the cart (order-level, kept while any personalised line remains).
        if (rushControl) {
          if (props['Rush'] === 'Yes' && !this.rushDormant) {
            rushControl.element.show();
            rushControl.checkbox.checked = true;
            rushControl.element.updateProductProperty(true);
          } else {
            rushControl.checkbox.checked = false;
            rushControl.element.updateProductProperty(false);
          }
        }

        // Arm the replacement with the line's full identity, not just its key: keys churn when
        // the cart's automatic-discount state changes, so replaceEditedLine may need to re-find
        // this line by fingerprint (variant + selling plan + properties) after the re-add.
        this._npEditSnapshot = {
          key: key,
          variantId: line.variant_id,
          sellingPlanId: npSellingPlanId(line),
          properties: line.properties,
          // The in-place edit path (inPlaceEdit) only handles a quantity-1 line; see tryInPlaceEdit.
          quantity: line.quantity
        };

        // The snapshot armed above deliberately SURVIVES "Remove personalization" (close({
        // keepEditSnapshot: true }), :176) and is consumed by the plain add that follows, which
        // onProductAdd now reaches because its `panel.hidden` early return exempts an armed
        // snapshot. Without that, the de-personalizing add posted a plain garment while nothing
        // retired the original line: the shopper got BOTH and kept paying the $65 rush attached to
        // the old one (found live 2026-07-30, measured $353.00 -> $557.00; see QA-LOG).
        //
        // Note what was NOT done: hiding the remove button while the snapshot is armed was built,
        // QA'd, and REJECTED before shipping. On an add error the theme fires
        // theme:product:add-error, not theme:product:add, so onProductAdd and therefore close()
        // never run; with the remove button hidden AND openBtn hidden by open(), the shopper has no
        // way out but a reload. Any future change here must leave at least one exit from an open
        // panel that does not depend on a successful add.

        // Instrumentation (D: decides the Horizon in-cart question with data). Fail-open: the
        // theme ships no dataLayer of its own; if GTM injected one, report the use, else silence.
        if (window.dataLayer && typeof window.dataLayer.push === 'function') {
          window.dataLayer.push({ event: 'np_edit_used', np_product_id: line.product_id });
        }

        // Consume the param only on success (a refresh must not re-trigger a stale prefill), and
        // surgically: variant, view, preview_theme_id, and hash all survive.
        const url = new URL(window.location.href);
        url.searchParams.delete('np_edit');
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        // Fail-open by contract. Validation-phase exceptions have touched nothing; an apply-phase
        // exception tears the panel back down (close() also disarms _npEditSnapshot) so a partially
        // prefilled panel can never be submitted as if it were the shopper's design. ALT-DRAWER:
        // close() alone no longer closes the visible surface (openDrawer() opened a <dialog>, not
        // the old inline panel), so the drawer must be force-closed too, or the shopper is left
        // staring at an open modal whose fields just went disabled and empty underneath them.
        console.warn('[native-personalizer] edit prefill aborted', error);
        if (applying) {
          try {
            this.close();
            if (this.drawer && this.drawer.open) {
              this.drawer.removeAttribute('closing');
              this.drawerClosing = false;
              this.drawer.close();
            }
          } catch (teardownError) {
            this._npEditSnapshot = null;
          }
        }
      }
    }

    // ============================================================================================
    // BUNDLE MODE (B2) public API. Usable in both modes; only the bundle host
    // (<alt-bundle-product data-bundle-scope>, built in B3) calls these. Standalone never calls
    // them and their behaviour there is unspecified/unused, not guaranteed byte-identical to
    // anything (the frozen contract is about cart/order output, not this internal surface).
    // ============================================================================================

    // Bundle mode only (self-guarded: a no-op with no bundleScope, so calling it from standalone
    // code paths is harmless). Decides the ONE shared <rush-personalization> element's visibility
    // for the whole bundle: shown while at least one AVAILABLE component carries a CONFIRMED
    // personalization, hidden otherwise. Standalone has exactly one personalizer, so "this one
    // opened/closed" and "the page's rush state" are the same question (open()/close()'s own
    // direct rush.show()/hide() calls, unchanged in the else branches above); a bundle has up to
    // several, so the question can only be answered by counting across `this.bundleScope`.
    //
    // Called from open()/close()'s bundle branches (covers every open, confirm [confirmDrawer()
    // ends by calling open()], cancel-with-nothing-confirmed, and remove [removePersonalization()
    // ends by calling close()] for THIS instance), applyAvailability()'s bundle branch (a sold-out/
    // back-in-stock flip on any component can change the count without that component's own
    // open()/close() ever running), and resetAfterBundleAdd() (belt-and-braces; close() already
    // reaches its own bundle branch, which calls this).
    //
    // RushPersonalization analysis (assets/custom.js:67-288), reused unchanged here exactly as
    // standalone already reuses it:
    //   - show()/hide() (custom.js:120-126) only toggle the `data-open` attribute the component's
    //     own CSS reads; no cart/property side effect either way. Safe to call redundantly.
    //   - updateProductProperty(bool) (custom.js:98-118) resolves `this.form` (set once in the
    //     RushPersonalization constructor from `data-parent-id` -> the matching product-form) and
    //     adds/removes a bare `properties[Rush]` hidden input on ITS OWN form. Removing when none
    //     exists is a no-op (`if (rushPersoProperty) rushPersoProperty.remove()`), so calling it
    //     unconditionally on the hide path, same as standalone close(), is safe even when there was
    //     nothing to clear. This file never calls it with `true` in bundle mode: which of possibly
    //     several confirmed components a checked box's $65 fee should attach to is the bundle
    //     host's question to answer (building the cart items it actually posts), not this file's;
    //     syncBundleRush() only ever decides show/hide, and only ever CLEARS the property on hide.
    syncBundleRush() {
      if (!this.bundleScope) return;
      const rush = this.rush;
      if (!rush) return;
      // Found live on the staging bundle PDP (2026-09-24): this script can connect BEFORE
      // custom.js defines <rush-personalization> (script order on the page), so the element is
      // still an un-upgraded HTMLElement with no show()/hide(). Calling them threw inside
      // connectedCallback (via applyAvailability), which aborted the rest of connectedCallback
      // before the drawer buttons were wired: every bundle drawer was dead. Wait for the upgrade
      // and re-run instead. The fixture missed this because its stub was defined first.
      if (typeof rush.show !== 'function' || typeof rush.hide !== 'function') {
        if (!this._npRushWait && window.customElements && customElements.whenDefined) {
          this._npRushWait = true;
          customElements.whenDefined('rush-personalization').then(() => {
            this._npRushWait = false;
            this.syncBundleRush();
          });
        }
        return;
      }
      let confirmedAvailable = 0;
      this.bundleScope.querySelectorAll('native-personalizer').forEach((np) => {
        if (np.hidden) return; // sold out (applyAvailability's own signal)
        if (np.panel && !np.panel.hidden) confirmedAvailable += 1; // panel.hidden === false means confirmed, see the constructor comment
      });
      if (confirmedAvailable > 0) {
        rush.show();
        return;
      }
      rush.hide();
      const rushCheckbox = this.rushCheckbox;
      if (rushCheckbox) rushCheckbox.checked = false;
      rush.updateProductProperty(false);
    }

    // True while the drawer is genuinely open and NOT mid-close-animation: the exact same
    // condition onAtcCapture's click-gate uses (above) to block an add attempt while an
    // unconfirmed draft is showing. Reused rather than restated: bare `this.drawer.open` stays
    // true through the ~250-400ms post-confirm exit animation too, which must NOT read as "still
    // editing" (see onAtcCapture's own comment on `!this.drawerClosing` for why).
    isEditing() {
      return !!(this.drawer && this.drawer.open && !this.drawerClosing);
    }

    // Returns this instance's confirmed personalization as a plain object, or {} when there is
    // nothing to report: unavailable (sold out), never confirmed, or mid-edit (isEditing()). Calls
    // normalizeAllFields() first (the same normalizer onFormSubmit/onAtcCapture run before a
    // standalone submit) so a value confirmed via Enter or a direct tap, which never fires the
    // blur listener normalizeField() otherwise waits for, still reports the canonical characters
    // the studio will stitch.
    //
    // Walks the ACTIVE branch fieldset's own DOM descendants generically (same "what would
    // FormData send" semantics FormData itself uses: skip a disabled control, skip an unchecked
    // radio/checkbox) rather than hand-listing keys, so a future config-driven field (E1 new
    // colours, E3 a new type) needs no change here, and every existing hidden field (Personalization
    // Type) and radio group (Embroidery font/color, checked value only) is picked up for free. Walks
    // the fieldset's descendants rather than `form.elements`: these fields are owned by the sibling
    // form via `form="…"` attribute association (np_input_form, B1), not by DOM nesting inside a
    // <form>, so `form.elements` would miss them entirely (same reasoning as buildGarmentItem()'s
    // own comment on why it uses FormData against the real product form instead of a subtree query).
    getConfirmedProperties() {
      this.normalizeAllFields();
      if (this.hidden) return {}; // unavailable (sold out)
      if (!this.panel || this.panel.hidden) return {}; // never confirmed
      if (this.isEditing()) return {}; // confirmed once, but a re-edit (the pencil) is in progress
      const branch = this.activeBranch();
      const fieldset = branch ? this.querySelector('fieldset[data-np-branch="' + branch + '"]') : null;
      if (!fieldset) return {};
      const props = {};
      fieldset.querySelectorAll('input, select, textarea').forEach((el) => {
        if (el.disabled) return;
        const match = /^properties\[(.+)\]$/.exec(el.name || '');
        if (!match) return;
        if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
        props[match[1]] = el.value;
      });
      return props;
    }

    // Called by the bundle host after a successful bundle add-to-cart. Standalone's equivalent
    // moment is what onProductAdd ends with (this.close()), MINUS the edit-snapshot/replace-line
    // logic that runs before it there: that logic exists to retire ONE cart line after a shopper
    // follows a cart "Edit personalization" link, which structurally cannot address a bundle line
    // (cart edit links exclude bundle lines by design, BUILD.md R5's `__bundle*` exclusion; and
    // onProductAdd itself is never wired to theme:product:add in bundle mode, item 2 above, so it
    // could not run this logic even if a snapshot were somehow armed). close() already drops any
    // armed _npEditSnapshot unconditionally (called with no options, so keepEditSnapshot is falsy),
    // so there is nothing bundle-specific left to clean up beyond calling it.
    resetAfterBundleAdd() {
      this.close();
      this.syncBundleRush();
    }
  }

  customElements.define('native-personalizer', NativePersonalizer);
}
